import { all, call, put, select, takeLatest, takeEvery, fork } from 'redux-saga/effects';
import type { SagaIterator } from '@redux-saga/types';
import { CREDENTIALS } from '../../constants';
import { getCredentials } from './cateringCredentials';
import { cateringPackagesSaga } from './cateringPackagesIndex';
import { cateringAccessSaga } from './cateringAccessIndex';
import { CL, CE, CO, CC, CI, COT, CS, CCT, CT, CRT, resolvedTemplateKey } from './cateringTypes';
import type { OrderChargePayload, OrderTaxPayload, OrderPayloadLineItem, FetchTaxesRequestAction, TaxItem } from './cateringTypes';
import * as A from './cateringActions';
import type { RootState } from '../rootReducer';
import {
  deleteCateringEntityApi,
  listLeadsApi,
  listLinkableLeadsApi,
  listLeadsBoardApi,
  listLeadsBoardColumnApi,
  getLeadDetailApi,
  createLeadApi,
  updateLeadApi,
  transitionLeadStatusApi,
  convertLeadToOrderApi,
  // getLeadsAnalyticsApi,
  listEstimatesApi,
  createEstimateApi,
  updateEstimateApi,
  updateEstimateStatusApi,
  fetchCatalogItemsApi,
  getEstimateDetailApi,
  getPublicEstimateDetailApi,
  sendEstimateApi,
  listOrdersApi,
  getOrderDayCapacityApi,
  getOrderDetailApi,
  createOrderApi,
  updateOrderApi,
  orderActionApi,
  transitionOrderStatusApi,
  sendOrderApi,
  listCustomersApi,
  getCustomerDetailApi,
  updateCustomerApi,
  deleteCustomerApi,
  listInvoicesApi,
  createInvoiceApi,
  getInvoiceDetailApi,
  updateInvoiceApi,
  notifyInvoiceApi,
  getOrderTypeFeesApi,
  getSettingApi,
  getAllSettingsApi,
  upsertMultipleSettingsApi,
  sendPaymentReminderApi,
  recordInvoicePaymentApi,
  listCommTemplatesApi,
  upsertCommTemplateApi,
  restoreCommTemplateApi,
  getTemplateTagsApi,
  fetchTaxesApi,
  resolveNotificationTemplateApi,
} from '../../services/cateringApi';
import type {
  ApiLeadListItem,
  ApiLeadDetail,
  ApiEstimateListItem,
  ApiEstimateDetail,
  CatalogItem,
  ApiOrderListItem,
  ApiOrderDetail,
  ApiOrderWireItem,
  OrderActionBody,
  ApiCustomer,
  ApiInvoiceListItem,
  ApiInvoiceDetail,
  ApiOrderTypeFee,
  ApiCommTemplate,
  TemplateTagsResponse,
  ResolvedNotificationTemplate,
  UpdateLineItemBody,
  SendSkip,
} from '../../services/cateringApi';
import type {
  CateringLead,
  LeadStatus,
  CateringEstimate,
  EstimateLineItem,
  AppliedCharge,
  AppliedTax,
  CateringOrder,
  OrderStatus,
  OrderLineItem,
  StatusEntry,
  CateringEventType,
  CancellationReason,
  CateringCustomer,
  CateringInvoice,
  Payment,
} from '../../components/catering/data/types';
import { parseAddress, trimAddress } from '../../components/catering/utils/addressUtils';
import { sortLineItemsAddonLast } from '../../components/catering/utils/lineItemSort';
import { buildItemScope, itemScopeFields, COVERS_ALL } from '../../components/catering/utils/itemScope';
import {
  mapApiPackageEstimateLine,
  mapApiPackageOrderLine,
  toEstimateWireLine,
  toOrderWireItem,
} from '../../components/catering/utils/lineItemWire';
import { isPackageApiLine } from '../../components/catering/utils/packageLines';
import { normalizePackageItems } from '../../components/catering/utils/packageItems';
import { normalizeLeadStatus } from '../../components/catering/data/cateringLeadConstants';

// `getCredentials` (session location / JWT / staff hint) lives in ./cateringCredentials so the
// packages saga can share it without a circular import.

// ════════════════════════════════════════════════════════════════════════════════
// LEADS SAGA
// ════════════════════════════════════════════════════════════════════════════════

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SOURCE_LABELS: Record<string, string> = {
  web_form: 'Web Form', website: 'Website', phone: 'Phone', email: 'Email',
  walk_in: 'Walk-In', referral: 'Referral',
};

function resolveChangedBy(value: string | null | undefined): string {
  if (!value) return 'System';
  if (UUID_RE.test(value)) return 'Staff';
  return SOURCE_LABELS[value.toLowerCase()] ?? value;
}

function mapListItemToLead(item: ApiLeadListItem): CateringLead {
  const firstName = item.firstName ?? '';
  const lastName = item.lastName ?? '';
  return {
    id: item.id,
    firstName,
    lastName,
    contactName: `${firstName} ${lastName}`.trim(),
    email: item.email ?? '',
    phone: item.phone ?? '',
    company: item.company ?? '',
    address: parseAddress(item.address),
    eventName: item.eventName ?? '',
    eventType: (item.eventType ?? '') as CateringLead['eventType'],
    eventTypeOther: item.eventTypeOther ?? '',
    estimatedHeadcount: item.estimatedHeadcount ?? null,
    estimatedDate: item.estimatedDate ?? null,
    estimatedTime: item.estimatedTime ?? null,
    budgetMin: item.budgetMin ?? null,
    budgetMax: item.budgetMax ?? null,
    notes: item.notes ?? '',
    status: normalizeLeadStatus(item.status),
    source: (item.source ?? '') as CateringLead['source'],
    followUpDate: item.followUpDate ?? null,
    linkedEstimates: item.linkedEstimates.map((e) => ({
      id: e.id,
      estimateNumber: e.estimateNumber,
      status: e.status,
      total: e.total,
      createdAt: e.createdAt,
    })),
    estimateIds: item.linkedEstimates.map((e) => e.id),
    estimateId: item.linkedEstimates[0]?.id ?? null,
    linkedOrders: (item.linkedOrders ?? []).map((o) => ({ id: o.id, orderId: o.orderId, status: o.status, total: o.total, createdAt: o.createdAt })),
    convertedOrderId: item.convertedOrderId ?? null,
    lostReason: (item.lostReason ?? null) as CateringLead['lostReason'],
    statusHistory: [],
    communicationLog: [],
    createdAt: item.createdAt ?? new Date().toISOString(),
    updatedAt: item.updatedAt ?? item.createdAt ?? new Date().toISOString(),
  };
}

function mapDetailToLead(d: ApiLeadDetail): CateringLead {
  const firstName = d.firstName ?? '';
  const lastName = d.lastName ?? '';
  return {
    id: d.id,
    firstName,
    lastName,
    contactName: `${firstName} ${lastName}`.trim(),
    email: d.email ?? '',
    phone: d.phone ?? '',
    company: d.company ?? '',
    address: parseAddress(d.address),
    eventName: d.eventName ?? '',
    eventType: (d.eventType ?? '') as CateringLead['eventType'],
    eventTypeOther: d.eventTypeOther ?? '',
    estimatedHeadcount: d.estimatedHeadcount ?? null,
    estimatedDate: d.estimatedDate ?? null,
    estimatedTime: d.estimatedTime ?? null,
    budgetMin: d.budgetMin ?? null,
    budgetMax: d.budgetMax ?? null,
    notes: d.notes ?? '',
    status: normalizeLeadStatus(d.status),
    source: (d.source ?? '') as CateringLead['source'],
    followUpDate: d.followUpDate ?? null,
    linkedEstimates: (d.linkedEstimates ?? []).map((e: any) => ({
      id: e.id,
      estimateNumber: e.estimateNumber ?? null,
      status: e.status ?? 'draft',
      total: e.total ?? 0,
      createdAt: e.createdAt ?? null,
    })),
    estimateIds: (d.linkedEstimates ?? []).map((e: any) => e.id),
    estimateId: (d.linkedEstimates ?? [])[0]?.id ?? null,
    linkedOrders: (d.linkedOrders ?? []).map((o: any) => ({ id: o.id, orderId: o.orderId ?? null, status: o.status ?? 'confirmed', total: o.total ?? 0, createdAt: o.createdAt ?? null })),
    convertedOrderId: d.convertedOrderId ?? null,
    lostReason: (d.lostReason ?? null) as CateringLead['lostReason'],
    statusHistory: (d.statusHistory ?? []).map((s) => ({
      status: s.toStatus ?? '',
      timestamp: s.changedAt ?? new Date().toISOString(),
      changedBy: resolveChangedBy(s.changedBy),
      note: s.note ?? undefined,
      source: s.source ?? undefined,
    })),
    communicationLog: (d.communicationLog ?? []).map((c) => ({
      id: c.id,
      channel: (c.channel ?? 'email') as 'email' | 'sms',
      templateKey: 'estimate_sent' as CateringLead['communicationLog'][number]['templateKey'],
      subject: c.subject ?? undefined,
      body: c.subject ?? '',
      sentAt: c.sentAt ?? new Date().toISOString(),
      sentBy: c.sentBy ?? 'System',
    })),
    createdAt: d.createdAt ?? new Date().toISOString(),
    updatedAt: d.updatedAt ?? new Date().toISOString(),
  };
}

let lastLeadsListPayload: ReturnType<typeof A.fetchLeadsRequest>['payload'] = {};

function* fetchLeadsWorker(action: ReturnType<typeof A.fetchLeadsRequest>): SagaIterator {
  try {
    lastLeadsListPayload = action.payload;
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchLeadsFailure('No locationId available'));
      return;
    }
    const { status, query, from, to, page, limit, sort, order } = action.payload;
    const response: Awaited<ReturnType<typeof listLeadsApi>> = yield call(
      listLeadsApi,
      locationId,
      resolvedToken,
      { status, query, from, to, page, limit, sort, order },
    );
    const leads = (response?.data ?? []).map(mapListItemToLead);
    yield put(A.fetchLeadsSuccess(leads, locationId, response.page));
  } catch (e) {
    yield put(A.fetchLeadsFailure(e instanceof Error ? e.message : 'Failed to fetch leads'));
  }
}

function* fetchLinkableLeadsWorker(action: ReturnType<typeof A.fetchLinkableLeadsRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchLinkableLeadsFailure('No locationId available'));
      return;
    }
    // Dedicated endpoint: backend returns all leads for the location except WON/LOST.
    const response: Awaited<ReturnType<typeof listLinkableLeadsApi>> = yield call(
      listLinkableLeadsApi,
      locationId,
      resolvedToken,
    );
    const leads = (response?.data ?? []).map(mapListItemToLead);
    yield put(A.fetchLinkableLeadsSuccess(leads, locationId));
  } catch (e) {
    yield put(A.fetchLinkableLeadsFailure(e instanceof Error ? e.message : 'Failed to fetch leads'));
  }
}

function* fetchLeadsBoardWorker(action: ReturnType<typeof A.fetchLeadsBoardRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchLeadsBoardFailure('No locationId available'));
      return;
    }
    const { query, from, to, limit, filterKey } = action.payload;
    const response: Awaited<ReturnType<typeof listLeadsBoardApi>> = yield call(
      listLeadsBoardApi,
      locationId,
      resolvedToken,
      { query, from, to, limit },
    );
    const columns = (response?.columns ?? []).map((c) => ({
      status: c.status as LeadStatus,
      items: (c.items ?? []).map(mapListItemToLead),
      page: c.page,
      total: c.total,
      hasMore: c.hasMore,
    }));
    yield put(A.fetchLeadsBoardSuccess(columns, locationId, filterKey));
  } catch (e) {
    yield put(A.fetchLeadsBoardFailure(e instanceof Error ? e.message : 'Failed to fetch board'));
  }
}

function* fetchBoardColumnWorker(action: ReturnType<typeof A.fetchBoardColumnRequest>): SagaIterator {
  const { status } = action.payload;
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchBoardColumnFailure(status, 'No locationId available'));
      return;
    }
    const { page, query, from, to, limit } = action.payload;
    const col: Awaited<ReturnType<typeof listLeadsBoardColumnApi>> = yield call(
      listLeadsBoardColumnApi,
      locationId,
      resolvedToken,
      status,
      page,
      { query, from, to, limit },
    );
    const items = (col?.items ?? []).map(mapListItemToLead);
    yield put(A.fetchBoardColumnSuccess(status, items, col.page, col.total, col.hasMore));
  } catch (e) {
    yield put(A.fetchBoardColumnFailure(status, e instanceof Error ? e.message : 'Failed to load more'));
  }
}

function* createLeadWorker(action: ReturnType<typeof A.createLeadRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.data.locationId || savedLocationId;
    const response: Awaited<ReturnType<typeof createLeadApi>> = yield call(
      createLeadApi,
      { ...action.payload.data, locationId },
      token,
    );
    const detail: Awaited<ReturnType<typeof getLeadDetailApi>> = yield call(
      getLeadDetailApi,
      response.leadId,
      token,
    );
    yield put(A.createLeadSuccess(mapDetailToLead(detail)));
  } catch (e) {
    yield put(A.createLeadFailure(e instanceof Error ? e.message : 'Failed to create lead'));
  }
}

function* updateLeadWorker(action: ReturnType<typeof A.updateLeadRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId || savedLocationId;
    yield call(
      updateLeadApi,
      action.payload.leadId,
      { ...action.payload.data, locationId },
      token,
    );
    yield put(A.updateLeadSuccess(action.payload.leadId, action.payload.data as Partial<CateringLead>));
    yield put(A.fetchLeadDetailRequest(action.payload.leadId));
  } catch (e) {
    yield put(A.updateLeadFailure(e instanceof Error ? e.message : 'Failed to update lead'));
  }
}

function* transitionStatusWorker(action: ReturnType<typeof A.transitionStatusRequest>): SagaIterator {
  const { leadId, status, prevStatus, lostReason, note } = action.payload;
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    yield call(
      transitionLeadStatusApi,
      leadId,
      {
        locationId,
        status,
        ...(lostReason ? { lostReason } : {}),
        ...(note ? { note } : {}),
      },
      token,
    );
    yield put(A.transitionStatusSuccess(leadId, status, prevStatus));
    yield put(A.fetchLeadDetailRequest(leadId));
  } catch (e) {
    yield put(A.transitionStatusFailure(e instanceof Error ? e.message : 'Failed to update status', leadId, prevStatus));
  }
}

function* convertLeadWorker(action: ReturnType<typeof A.convertLeadRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const response: Awaited<ReturnType<typeof convertLeadToOrderApi>> = yield call(
      convertLeadToOrderApi,
      action.payload.leadId,
      { locationId },
      token,
    );
    const orderId =
      (response as any)?.orderId ??
      (response as any)?.orderDbId ??
      (response as any)?.id ??
      action.payload.leadId;
    yield put(A.convertLeadSuccess(action.payload.leadId, String(orderId)));
    yield put(A.fetchLeadDetailRequest(action.payload.leadId));
  } catch (e) {
    yield put(A.convertLeadFailure(e instanceof Error ? e.message : 'Failed to convert lead'));
  }
}

function* deleteLeadWorker(action: ReturnType<typeof A.deleteLeadRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    yield call(deleteCateringEntityApi, 'leads', action.payload.leadId, savedLocationId, token);
    yield put(A.deleteLeadSuccess(action.payload.leadId));
  } catch (e) {
    yield put(A.deleteLeadFailure(e instanceof Error ? e.message : 'Failed to delete lead'));
  }
}

function* fetchLeadDetailWorker(action: ReturnType<typeof A.fetchLeadDetailRequest>): SagaIterator {
  try {
    const { token } = getCredentials();
    const detail: Awaited<ReturnType<typeof getLeadDetailApi>> = yield call(
      getLeadDetailApi,
      action.payload.leadId,
      token,
    );
    yield put(A.fetchLeadDetailSuccess(mapDetailToLead(detail)));
  } catch (e) {
    yield put(A.fetchLeadDetailFailure(e instanceof Error ? e.message : 'Failed to fetch lead'));
  }
}

// function* fetchAnalyticsWorker(action: ReturnType<typeof A.fetchAnalyticsRequest>): SagaIterator {
//   try {
//     const { locationId: savedLocationId, token } = getCredentials();
//     const locationId = action.payload.locationId ?? savedLocationId;
//     const resolvedToken = action.payload.token ?? token;
//     if (!locationId) {
//       yield put(A.fetchAnalyticsFailure('No locationId available'));
//       return;
//     }
//     const analytics: Awaited<ReturnType<typeof getLeadsAnalyticsApi>> = yield call(
//       getLeadsAnalyticsApi,
//       locationId,
//       resolvedToken,
//     );
//     yield put(A.fetchAnalyticsSuccess(analytics));
//   } catch (e) {
//     yield put(A.fetchAnalyticsFailure(e instanceof Error ? e.message : 'Failed to fetch analytics'));
//   }
// }

function* refetchAnalyticsAfterMutation(): SagaIterator {
  const { locationId, token } = getCredentials();
  if (!locationId) return;
  yield put(A.fetchAnalyticsRequest({ locationId, token }));
}

function* refetchLeadsListAfterMutation(): SagaIterator {
  yield put(A.fetchLeadsRequest(lastLeadsListPayload));
}

function* cateringLeadsSaga(): SagaIterator {
  yield all([
    takeLatest(CL.FETCH_REQUEST, fetchLeadsWorker),
    takeLatest(CL.LINKABLE_FETCH_REQUEST, fetchLinkableLeadsWorker),
    takeLatest(CL.BOARD_FETCH_REQUEST, fetchLeadsBoardWorker),
    takeEvery(CL.BOARD_COLUMN_REQUEST, fetchBoardColumnWorker),
    // takeLatest(CL.FETCH_ANALYTICS_REQUEST, fetchAnalyticsWorker),
    takeEvery(CL.FETCH_DETAIL_REQUEST, fetchLeadDetailWorker),
    takeEvery(CL.DELETE_REQUEST, deleteLeadWorker),
    takeEvery(CL.CREATE_REQUEST, createLeadWorker),
    takeEvery(CL.UPDATE_REQUEST, updateLeadWorker),
    takeEvery(CL.STATUS_REQUEST, transitionStatusWorker),
    takeEvery(CL.CONVERT_REQUEST, convertLeadWorker),
    takeEvery(
      [CL.CREATE_SUCCESS, CL.UPDATE_SUCCESS, CL.STATUS_SUCCESS, CL.CONVERT_SUCCESS, CL.DELETE_SUCCESS],
      refetchAnalyticsAfterMutation,
    ),
    takeLatest(
      [CL.CREATE_SUCCESS, CL.UPDATE_SUCCESS, CL.STATUS_SUCCESS, CL.CONVERT_SUCCESS, CL.DELETE_SUCCESS],
      refetchLeadsListAfterMutation,
    ),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// ESTIMATES SAGA
// ════════════════════════════════════════════════════════════════════════════════

function mapListItemToEstimate(item: ApiEstimateListItem): CateringEstimate {
  return {
    id: item.id,
    estimateNumber: item.estimateNumber,
    leadId: null,
    orderId: item.orderId ?? null,
    status: (item.status ?? 'draft') as CateringEstimate['status'],
    customerFirstName: item.customerFirstName ?? '',
    customerLastName: item.customerLastName ?? '',
    customerEmail: '',
    customerPhone: '',
    company: item.company ?? '',
    eventName: item.eventName ?? '',
    eventDate: item.eventDate ?? '',
    eventTime: '',
    eventType: 'corporate' as CateringEstimate['eventType'],
    eventTypeOther: '',
    orderTypeId: null,
    orderType: '',
    headcount: item.headcount ?? 0,
    lineItems: [],
    subtotal: 0,
    discount: null as any,
    taxExempt: false,
    serviceCharge: 0,
    appliedCharges: [],
    total: parseFloat(item.total) || 0,
    validUntil: item.validUntil ?? '',
    contractTerms: '',
    instructions: '',
    dietaryRestrictions: [],
    sentAt: null,
    approvedAt: null,
    rejectedAt: null,
    statusHistory: [],
    notes: '',
    communicationLog: [],
    createdAt: item.createdAt,
    updatedAt: item.createdAt,
  };
}

let lastEstimatesListPayload: ReturnType<typeof A.fetchEstimatesRequest>['payload'] = {};

function* fetchEstimatesWorker(action: ReturnType<typeof A.fetchEstimatesRequest>): SagaIterator {
  try {
    lastEstimatesListPayload = action.payload;
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchEstimatesFailure('No locationId available'));
      return;
    }
    const { status, query, from, to, page, limit, sort, order } = action.payload;
    const response: Awaited<ReturnType<typeof listEstimatesApi>> = yield call(
      listEstimatesApi,
      locationId,
      resolvedToken,
      { status, query, from, to, page, limit, sort, order },
    );
    const estimates = (response?.data ?? []).map(mapListItemToEstimate);
    yield put(A.fetchEstimatesSuccess(estimates, locationId, response.page));
  } catch (e) {
    yield put(A.fetchEstimatesFailure(e instanceof Error ? e.message : 'Failed to fetch estimates'));
  }
}

function* createEstimateWorker(action: ReturnType<typeof A.createEstimateRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    const { data } = action.payload;

    if (!locationId) {
      yield put(A.createEstimateFailure('No locationId available'));
      return;
    }

    // Addons no longer render last automatically server-side — reconstruct that ordering
    // ourselves by sorting addon-tagged rows after everything else before assigning sortOrder.
    const validLineItems = sortLineItemsAddonLast(data.lineItems.filter((li) => li.name.trim()));
    // Annotated with the wire type on purpose: a contextual return type makes
    // excess-property checking fire, so an FE-internal field (`inDefaultTaxBase`) is a
    // compile error rather than a silent leak onto the wire.
    // Package lines go out as `type:'package'` (itemId = package id, unitPrice, packageItems, ...) —
    // see `toEstimateWireLine`; every other line is unchanged.
    const backendLineItems = validLineItems.map((li, idx): UpdateLineItemBody => toEstimateWireLine(li, idx));

    // Item coverage must be computed against the array we actually send: `backendLineItems`
    // is index-aligned with `validLineItems` (same filter, same addon-last sort), and
    // `itemIndexes` are positions in that array.
    const chargeScope = itemScopeFields(buildItemScope(backendLineItems, COVERS_ALL));
    // Order-level taxes cover exactly the lines behind `defaultTaxableSubtotal`. The flag is
    // set by EstimateFormPage from the live catalog — a missing flag (older payload shape)
    // falls back to "covered", matching the pre-contract backend behaviour.
    const defaultTaxScope = itemScopeFields(
      buildItemScope(backendLineItems, (_li, i) => validLineItems[i]?.inDefaultTaxBase !== false),
    );

    const activeCharges = data.appliedCharges
      .filter((c) => !c.removed && !!c.sourceChargeId && (c.rate ?? c.chargeValue ?? 0) !== 0)
      .map((c) => ({
        taxfeesId: c.sourceChargeId || undefined,
        name: c.name,
        type: (c.chargeType as 'PERCENT' | 'FLATFEE') || undefined,
        value: c.chargeValue ?? c.rate,
        amount: c.amount,
        isTaxable: c.taxable,
        ...chargeScope,
      }));

    const activeTaxes = (data.appliedTaxes ?? [])
      .filter((t) => !t.exempt)
      .map((t) => {
        const rawId = t.taxfeesId ?? t.id ?? '';
        const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
        return {
          taxfeesId,
          name: t.name,
          type: t.type,
          value: t.rate,
          amount: t.amount ?? 0,
          isTaxable: t.isTaxable ?? false,
          isItemTax: t.isItemTax ?? false,
          // Item taxes already carry resolved ids from deriveItemTaxes (item-taxed lines are
          // always catalog lines, so they never need indexes). Order-level taxes get the
          // default tax-base scope — never their read-back `itemIds`, which would silently
          // narrow coverage to whatever was stored last time.
          ...(t.isItemTax
            ? (t.itemIds?.length ? { itemIds: t.itemIds } : {})
            : defaultTaxScope),
        };
      });

    const discount = data.discount.type !== 'none'
      ? {
          type: data.discount.type === 'percentage' ? 'PERCENT' : data.discount.type === 'fixed' ? 'FLATFEE' : data.discount.type,
          value: data.discount.value,
          ...(data.discount.reason?.trim() ? { reason: data.discount.reason } : {}),
        }
      : undefined;

    const created: Awaited<ReturnType<typeof createEstimateApi>> = yield call(
      createEstimateApi,
      {
        locationId,
        ...(data.leadId ? { leadId: data.leadId } : {}),
        customerFirstName: data.customerFirstName || undefined,
        customerLastName:  data.customerLastName  || undefined,
        customerEmail:     data.customerEmail     || undefined,
        customerPhone:     data.customerPhone     || undefined,
        company:           data.company           || undefined,
        ...(data.address ? { address: data.address } : {}),
        eventName:         data.eventName         || undefined,
        eventType:         data.eventType         || undefined,
        eventTypeOther:    data.eventTypeOther    || undefined,
        eventDate: data.eventDate,
        // Optional — null, never '' (an empty string passes backend validation but
        // persists a blank time).
        eventTime: data.eventTime?.trim() || null,
        ...(data.dispatchTime ? { dispatchTime: data.dispatchTime } : {}),
        ...(data.servingTime ? { servingTime: data.servingTime } : {}),
        ...(data.gstin ? { gstin: data.gstin } : {}),
        orderTypeId: data.orderTypeId,
        // Optional per the backend contract — send only when actually entered.
        ...(data.headcount ? { headcount: data.headcount } : {}),
        validUntil: data.validUntil || undefined,
        lineItems: backendLineItems,
        discount,
        appliedCharges: activeCharges,
        appliedTaxes: activeTaxes,
        ...(data.contractTerms?.trim() ? { contractTerms: data.contractTerms } : {}),
        ...(data.instructions?.trim() ? { instructions: data.instructions } : {}),
        ...(data.notes?.trim() ? { notes: data.notes } : {}),
        dietaryRestrictions: data.dietaryRestrictions ?? [],
      },
      resolvedToken,
    );

    const newEstimate: CateringEstimate = {
      id: created.estimateId,
      estimateNumber: created.estimateNumber,
      leadId: data.leadId ?? null,
      orderId: null,
      status: 'draft',
      customerFirstName: data.customerFirstName,
      customerLastName: data.customerLastName,
      customerEmail: data.customerEmail,
      customerPhone: data.customerPhone,
      company: data.company,
      address: data.address ?? {},
      eventName: data.eventName,
      eventDate: data.eventDate,
      eventTime: data.eventTime,
      dispatchTime: data.dispatchTime,
      servingTime: data.servingTime,
      gstin: data.gstin,
      eventType: data.eventType as CateringEstimate['eventType'],
      eventTypeOther: data.eventTypeOther,
      orderTypeId: data.orderTypeId ?? null,
      orderType: data.orderType ?? '',
      headcount: data.headcount,
      lineItems: data.lineItems,
      subtotal: data.subtotal,
      discount: data.discount,
      taxExempt: data.taxExempt,
      serviceCharge: data.serviceCharge,
      appliedCharges: data.appliedCharges,
      appliedTaxes: data.appliedTaxes,
      total: data.total,
      validUntil: data.validUntil,
      contractTerms: data.contractTerms ?? '',
      instructions: data.instructions ?? '',
      dietaryRestrictions: data.dietaryRestrictions ?? [],
      sentAt: null,
      approvedAt: null,
      rejectedAt: null,
      statusHistory: [],
      notes: data.notes ?? '',
      communicationLog: [],
      createdAt: created.createdAt,
      updatedAt: created.createdAt,
    };

    yield put(A.createEstimateSuccess(newEstimate));

    if (data.sendImmediately) {
      yield put(A.sendEstimateRequest(created.estimateId, { channels: ['email', 'sms'] }));
    }
  } catch (e) {
    yield put(A.createEstimateFailure(e instanceof Error ? e.message : 'Failed to create estimate'));
  }
}

function* updateEstimateWorker(action: ReturnType<typeof A.updateEstimateRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    const { id, data } = action.payload;

    if (!locationId) {
      yield put(A.updateEstimateFailure('No locationId available'));
      return;
    }

    // Addons no longer render last automatically server-side — reconstruct that ordering
    // ourselves by sorting addon-tagged rows after everything else before assigning sortOrder.
    const validLineItems = sortLineItemsAddonLast((data.lineItems ?? []).filter((li) => li.name.trim() || li.itemId));
    // Annotated with the wire type on purpose: a contextual return type makes
    // excess-property checking fire, so an FE-internal field (`inDefaultTaxBase`) is a
    // compile error rather than a silent leak onto the wire.
    // Package lines go out as `type:'package'` (itemId = package id, unitPrice, packageItems, ...) —
    // see `toEstimateWireLine`; every other line is unchanged.
    const backendLineItems = validLineItems.map((li, idx): UpdateLineItemBody => toEstimateWireLine(li, idx));

    // Same rules as create — see createEstimateWorker. Coverage is recomputed from the
    // payload being sent now, never echoed from what was stored previously.
    const chargeScope = itemScopeFields(buildItemScope(backendLineItems, COVERS_ALL));
    const defaultTaxScope = itemScopeFields(
      buildItemScope(backendLineItems, (_li, i) => validLineItems[i]?.inDefaultTaxBase !== false),
    );

    const activeCharges = (data.appliedCharges ?? [])
      .filter((c) => !c.removed && !!c.sourceChargeId && (c.rate ?? c.chargeValue ?? 0) !== 0)
      .map((c) => ({
        ...(!c.id || c.id.startsWith('ac-') ? {} : { id: c.id }),
        taxfeesId: c.sourceChargeId || undefined,
        name: c.name,
        type: (c.chargeType as 'PERCENT' | 'FLATFEE') || undefined,
        value: c.chargeValue ?? c.rate,
        amount: c.amount,
        isTaxable: c.taxable,
        ...chargeScope,
      }));

    const activeTaxes = (data.appliedTaxes ?? [])
      .filter((t) => !t.exempt)
      .map((t) => {
        const rawId = t.taxfeesId ?? t.id ?? '';
        const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
        return {
          taxfeesId,
          name: t.name,
          type: t.type,
          value: t.rate,
          amount: t.amount ?? 0,
          isTaxable: t.isTaxable ?? false,
          isItemTax: t.isItemTax ?? false,
          // Item taxes already carry resolved ids from deriveItemTaxes (item-taxed lines are
          // always catalog lines, so they never need indexes). Order-level taxes get the
          // default tax-base scope — never their read-back `itemIds`, which would silently
          // narrow coverage to whatever was stored last time.
          ...(t.isItemTax
            ? (t.itemIds?.length ? { itemIds: t.itemIds } : {})
            : defaultTaxScope),
        };
      });

    // This is an update (PATCH), unlike create — a field omitted from the body means
    // "leave unchanged" server-side, not "clear it". So when the discount is reset to
    // 'none' it still has to be sent explicitly, or a previously-saved discount amount
    // never gets cleared and keeps being applied after the "update" appears to succeed.
    const discount = data.discount
      ? data.discount.type !== 'none'
        ? {
            type: data.discount.type === 'percentage' ? 'PERCENT' : data.discount.type === 'fixed' ? 'FLATFEE' : data.discount.type,
            value: data.discount.value,
            ...(data.discount.reason?.trim() ? { reason: data.discount.reason } : {}),
          }
        : { type: 'none', value: 0 }
      : undefined;

    yield call(
      updateEstimateApi,
      id,
      {
        locationId,
        customerFirstName: data.customerFirstName || undefined,
        customerLastName:  data.customerLastName  || undefined,
        customerEmail:     data.customerEmail     || undefined,
        customerPhone:     data.customerPhone     || undefined,
        company:           data.company           || undefined,
        ...(data.address ? { address: data.address } : {}),
        eventName:         data.eventName         || undefined,
        eventType:         data.eventType         || undefined,
        eventTypeOther:    data.eventTypeOther    || undefined,
        eventDate:         data.eventDate         || undefined,
        // Always sent (as null when cleared) — PATCH treats an omitted key as
        // "leave unchanged", so `|| undefined` would make clearing the time a no-op.
        eventTime:         data.eventTime?.trim() || null,
        dispatchTime:      data.dispatchTime      || undefined,
        servingTime:       data.servingTime       || undefined,
        gstin:             data.gstin             || undefined,
        ...(data.headcount !== undefined ? { headcount: data.headcount } : {}),
        ...(data.orderTypeId ? { orderTypeId: data.orderTypeId } : {}),
        lineItems: backendLineItems,
        discount,
        appliedCharges: activeCharges,
        appliedTaxes: activeTaxes,
        validUntil: data.validUntil || undefined,
        // PATCH semantics: an omitted key means "leave unchanged" server-side, not "clear
        // it" — so Contract Terms and Internal Notes must always be sent on update, even
        // as '', or clearing them in the form silently fails to persist.
        contractTerms: data.contractTerms ?? '',
        notes: data.notes ?? '',
        ...(data.instructions?.trim() ? { instructions: data.instructions } : {}),
        ...(data.dietaryRestrictions !== undefined ? { dietaryRestrictions: data.dietaryRestrictions } : {}),
      },
      resolvedToken,
    );

    const now = new Date().toISOString();
    const updatedEstimate: Partial<CateringEstimate> & { id: string } = {
      id,
      updatedAt: now,
      ...(data.headcount !== undefined ? { headcount: data.headcount } : {}),
      ...(data.lineItems !== undefined ? { lineItems: data.lineItems } : {}),
      ...(data.discount !== undefined ? { discount: data.discount } : {}),
      ...(data.appliedCharges !== undefined ? { appliedCharges: data.appliedCharges } : {}),
      ...(data.appliedTaxes !== undefined ? { appliedTaxes: data.appliedTaxes } : {}),
      ...(data.validUntil !== undefined ? { validUntil: data.validUntil } : {}),
      ...(data.subtotal !== undefined ? { subtotal: data.subtotal } : {}),
      ...(data.total !== undefined ? { total: data.total } : {}),
      ...(data.taxAmount !== undefined ? { taxAmount: data.taxAmount } : {}),
      ...(data.customerFirstName !== undefined ? { customerFirstName: data.customerFirstName } : {}),
      ...(data.customerLastName !== undefined ? { customerLastName: data.customerLastName } : {}),
      ...(data.address !== undefined ? { address: data.address } : {}),
      ...(data.eventName !== undefined ? { eventName: data.eventName } : {}),
      ...(data.eventDate !== undefined ? { eventDate: data.eventDate } : {}),
      ...(data.eventTime !== undefined ? { eventTime: data.eventTime } : {}),
      ...(data.dispatchTime !== undefined ? { dispatchTime: data.dispatchTime } : {}),
      ...(data.servingTime !== undefined ? { servingTime: data.servingTime } : {}),
      ...(data.gstin !== undefined ? { gstin: data.gstin } : {}),
      ...(data.orderTypeId !== undefined ? { orderTypeId: data.orderTypeId } : {}),
      ...(data.orderType !== undefined ? { orderType: data.orderType as string } : {}),
      ...(data.contractTerms !== undefined ? { contractTerms: data.contractTerms } : {}),
      ...(data.notes !== undefined ? { notes: data.notes } : {}),
      ...(data.dietaryRestrictions !== undefined ? { dietaryRestrictions: data.dietaryRestrictions } : {}),
    };

    yield put(A.updateEstimateSuccess(updatedEstimate as CateringEstimate));
    yield put(A.fetchEstimateDetailRequest(id));

    if (data.sendImmediately) {
      yield put(A.sendEstimateRequest(id, { channels: ['email', 'sms'] }));
    }
  } catch (e) {
    yield put(A.updateEstimateFailure(e instanceof Error ? e.message : 'Failed to update estimate'));
  }
}

function mapDetailToEstimate(d: ApiEstimateDetail): CateringEstimate {
  const discountTypeRaw = d.discount?.type?.toLowerCase() ?? 'none';
  const discountType = (['percentage', 'fixed'] as const).includes(discountTypeRaw as any)
    ? (discountTypeRaw as 'percentage' | 'fixed')
    : 'none';

  const lineItems: EstimateLineItem[] = (d.lineItems ?? []).map((li) => isPackageApiLine(li.type) ? mapApiPackageEstimateLine(li) : ({
    id: li.id,
    type: li.isAddonItem ? 'addon' : li.itemId && !li.isCustomItem ? 'item' : 'custom',
    isAddonItem: !!li.isAddonItem,
    itemId: li.isCustomItem || li.isAddonItem ? undefined : (li.itemId ?? undefined),
    sortOrder: li.sortOrder,
    isTaxExempt: !!li.isTaxExempt,
    name: li.name ?? '',
    quantity: parseFloat(li.quantity ?? '1') || 1,
    unitPrice: parseFloat(li.unitPrice ?? '0') || 0,
    total: parseFloat(li.total ?? '0') || 0,
    notes: li.notes ?? '',
    tierId: li.tierId ?? undefined,
    tierLabel: li.tierLabel ?? undefined,
  } as EstimateLineItem));

  const appliedCharges: AppliedCharge[] = (d.appliedCharges ?? []).map((c) => ({
    id: c.id,
    sourceChargeId: c.taxfeesId ?? '',
    name: c.name ?? '',
    chargeType: (c.type ?? undefined) as any,
    rate: c.value ?? undefined,
    chargeValue: c.value ?? undefined,
    amount: c.amount ?? 0,
    taxable: c.isTaxable,
    removed: false,
    itemIds: c.itemIds ?? [],
  }));

  const appliedTaxes: AppliedTax[] = (d.appliedTaxes ?? []).map((t) => {
    const isItem = !!t.isItemTax;
    const taxfeesId = t.taxfeesId ?? t.taxId;
    return {
      id: isItem ? `item:${taxfeesId}` : taxfeesId,
      taxfeesId: taxfeesId ?? undefined,
      name: t.name,
      type: (t.type ?? 'PERCENT') as 'PERCENT' | 'FLATFEE',
      rate: t.rate ?? 0,
      exempt: false,
      source: (isItem ? 'item' : 'order_type') as AppliedTax['source'],
      isItemTax: isItem,
      itemIds: t.itemIds ?? [],
      isTaxable: t.isTaxable,
      amount: t.amount ?? 0,
    };
  });

  return {
    id: d.id,
    estimateNumber: d.estimateNumber,
    leadId: d.leadId,
    orderId: d.orderId ?? null,
    status: (d.status ?? 'draft') as CateringEstimate['status'],
    customerFirstName: d.customerFirstName ?? '',
    customerLastName: d.customerLastName ?? '',
    customerEmail: d.customerEmail ?? '',
    customerPhone: d.customerPhone ?? '',
    company: d.company ?? '',
    address: parseAddress(d.address),
    eventName: d.eventName ?? '',
    eventDate: d.eventDate ?? '',
    eventTime: d.eventTime ?? '',
    dispatchTime: d.dispatchTime ?? undefined,
    servingTime: d.servingTime ?? undefined,
    gstin: d.gstin ?? undefined,
    eventType: (d.eventType ?? 'corporate') as CateringEstimate['eventType'],
    eventTypeOther: d.eventTypeOther ?? '',
    orderTypeId: d.orderType?.id ?? null,
    orderType: d.orderType?.name ?? '',
    headcount: d.headcount ?? 0,
    lineItems,
    subtotal: parseFloat(d.subtotal ?? '0') || 0,
    // When type is 'none' force value/amount to 0 rather than trusting whatever the API
    // echoed back — a discount cleared client-side but not fully zeroed out server-side
    // would otherwise keep showing/applying its old amount after the update "succeeds".
    discount: discountType === 'none'
      ? { type: 'none', value: 0, amount: 0, reason: d.discount?.reason ?? '' }
      : {
        type: discountType,
        value: parseFloat(d.discount?.value ?? '0') || 0,
        amount: parseFloat(d.discount?.amount ?? '0') || 0,
        reason: d.discount?.reason ?? '',
      },
    taxExempt: false,
    serviceCharge: appliedCharges.reduce((s, c) => s + c.amount, 0),
    appliedCharges,
    appliedTaxes,
    total: parseFloat(d.total ?? '0') || 0,
    validUntil: d.validUntil ?? '',
    contractTerms: d.contractTerms ?? '',
    instructions: d.instructions ?? '',
    dietaryRestrictions: d.dietaryRestrictions ?? [],
    sentAt: d.statusHistory?.find((s) => s.toStatus === 'sent')?.changedAt ?? null,
    approvedAt: d.statusHistory?.find((s) => s.toStatus === 'approved')?.changedAt ?? null,
    rejectedAt: d.statusHistory?.find((s) => s.toStatus === 'rejected')?.changedAt ?? null,
    statusHistory: (d.statusHistory ?? []).map((s) => ({
      fromStatus: (s.fromStatus ?? null) as CateringEstimate['status'] | null,
      toStatus: (s.toStatus ?? 'draft') as CateringEstimate['status'],
      changedBy: s.changedBy ?? '',
      note: s.note ?? undefined,
      source: s.source ?? null,
      changedAt: s.changedAt,
    })),
    notes: d.notes ?? '',
    communicationLog: [],
    createdAt: d.createdAt,
    updatedAt: d.updatedAt ?? d.createdAt,
  };
}

function* fetchEstimateDetailWorker(action: ReturnType<typeof A.fetchEstimateDetailRequest>): SagaIterator {
  try {
    const { token } = getCredentials();
    const raw: ApiEstimateDetail = token
      ? yield call(getEstimateDetailApi, action.payload.id, token)
      : yield call(getPublicEstimateDetailApi, action.payload.id);
    yield put(A.fetchEstimateDetailSuccess(mapDetailToEstimate(raw)));
  } catch (e) {
    yield put(A.fetchEstimateDetailFailure(e instanceof Error ? e.message : 'Failed to fetch estimate'));
  }
}

function* deleteEstimateWorker(action: ReturnType<typeof A.deleteEstimateRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    yield call(deleteCateringEntityApi, 'estimates', action.payload.estimateId, locationId, resolvedToken);
    yield put(A.deleteEstimateSuccess(action.payload.estimateId));
  } catch (e) {
    yield put(A.deleteEstimateFailure(e instanceof Error ? e.message : 'Failed to delete estimate'));
  }
}

function* fetchCatalogItemsWorker(): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.fetchCatalogItemsFailure('No locationId available'));
      return;
    }
    const items: CatalogItem[] = yield call(fetchCatalogItemsApi, locationId, token);
    yield put(A.fetchCatalogItemsSuccess(Array.isArray(items) ? items : []));
  } catch (e) {
    yield put(A.fetchCatalogItemsFailure(e instanceof Error ? e.message : 'Failed to fetch catalog items'));
  }
}

/**
 * Sends go out one channel per request, so fold the per-channel responses into one outcome:
 * `sent` if any channel really went out, skipped reasons concatenated. Status/sentAt come from
 * the response that actually sent (else the last one, where the server leaves them unchanged).
 */
function mergeSendOutcomes(
  responses: { sent?: boolean; skipped?: SendSkip[]; status: string; sentAt: string | null }[],
): { sent: boolean; skipped: SendSkip[]; status: string; sentAt: string | null } {
  const last = responses[responses.length - 1];
  const winner = [...responses].reverse().find((r) => r.sent === true) ?? last;
  return {
    sent: responses.some((r) => r.sent === true),
    skipped: responses.flatMap((r) => r.skipped ?? []),
    status: winner?.status ?? '',
    sentAt: winner?.sentAt ?? null,
  };
}

function* sendEstimateWorker(action: ReturnType<typeof A.sendEstimateRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.sendEstimateFailure('No locationId available'));
      return;
    }
    const { id, data } = action.payload;
    const channels = data.channels?.length ? data.channels : (['email'] as const);
    let response!: Awaited<ReturnType<typeof sendEstimateApi>>;
    const responses: Awaited<ReturnType<typeof sendEstimateApi>>[] = [];
    for (const channel of channels) {
      const channelBody = channel === 'sms' ? (data.smsBodyOverride ?? data.bodyOverride) : data.bodyOverride;
      response = yield call(
        sendEstimateApi,
        id,
        {
          locationId,
          channels: [channel],
          ...(channel === 'email' && data.subjectOverride ? { subjectOverride: data.subjectOverride } : {}),
          ...(channelBody ? { bodyOverride: channelBody } : {}),
          ...(channel === 'sms' && data.phoneOverride ? { phoneOverride: data.phoneOverride } : {}),
          ...(staffId ? { staffId } : {}),
        },
        token,
      );
      responses.push(response);
    }
    const outcome = mergeSendOutcomes(responses);
    yield put(A.sendEstimateSuccess(response.estimateId, {
      sent: outcome.sent,
      skipped: outcome.skipped,
      status: outcome.status,
      sentAt: outcome.sentAt,
    }));

    // Nothing went out — leave the lead and document untouched; the UI shows the warning.
    if (!outcome.sent) return;

    const estimates: CateringEstimate[] = yield select((s: any) => s.catering.estimates.estimates);
    const detail: CateringEstimate | null = yield select((s: any) => s.catering.estimates.detail);
    const estimate = detail?.id === id ? detail : estimates.find((e) => e.id === id);
    if (estimate?.leadId) {
      yield put(A.updateLeadSuccess(estimate.leadId, { status: 'estimate_sent' }));
    }
    yield put(A.fetchEstimateDetailRequest(id));
  } catch (e) {
    yield put(A.sendEstimateFailure(e instanceof Error ? e.message : 'Failed to send estimate'));
  }
}

function* updateEstimateStatusWorker(action: ReturnType<typeof A.updateEstimateStatusRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    const { id, action: statusAction } = action.payload;
    const response: Awaited<ReturnType<typeof updateEstimateStatusApi>> = yield call(
      updateEstimateStatusApi,
      id,
      { locationId, action: statusAction },
      token,
    );
    const derivedStatus = statusAction === 'approve' ? 'approved' : 'rejected';
    yield put(A.updateEstimateStatusSuccess(response.estimateId, derivedStatus, response.updatedAt));
    yield put(A.fetchEstimateDetailRequest(id));
  } catch (e) {
    yield put(A.updateEstimateStatusFailure(e instanceof Error ? e.message : 'Failed to update estimate status'));
  }
}

function* refetchEstimatesListAfterMutation(): SagaIterator {
  yield put(A.fetchEstimatesRequest(lastEstimatesListPayload));
}

function* cateringEstimatesSaga(): SagaIterator {
  yield all([
    takeLatest(CE.FETCH_REQUEST, fetchEstimatesWorker),
    takeLatest(CE.CREATE_REQUEST, createEstimateWorker),
    takeLatest(CE.UPDATE_REQUEST, updateEstimateWorker),
    takeLatest(CE.DELETE_REQUEST, deleteEstimateWorker),
    takeLatest(CE.FETCH_CATALOG_ITEMS_REQUEST, fetchCatalogItemsWorker),
    takeLatest(CE.FETCH_DETAIL_REQUEST, fetchEstimateDetailWorker),
    takeLatest(CE.SEND_REQUEST, sendEstimateWorker),
    takeLatest(CE.UPDATE_STATUS_REQUEST, updateEstimateStatusWorker),
    takeLatest(
      [CE.CREATE_SUCCESS, CE.UPDATE_SUCCESS, CE.SEND_SUCCESS, CE.UPDATE_STATUS_SUCCESS, CE.DELETE_SUCCESS],
      refetchEstimatesListAfterMutation,
    ),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS SAGA
// ════════════════════════════════════════════════════════════════════════════════

const safeOrderType = (v: string | null | undefined): string => v ?? '';

const safeStatus = (v: string | null | undefined): OrderStatus => {
  if (v === 'preparing') return 'in_prep';
  const allowed: OrderStatus[] = ['draft', 'confirmed', 'in_prep', 'ready', 'out_for_delivery', 'completed', 'cancelled'];
  if (v && allowed.includes(v as OrderStatus)) return v as OrderStatus;
  return 'confirmed';
};

const STATUS_COMMENT_LABELS: Record<string, string> = {
  customer_request: 'Customer request',
  merchant_unavailable: 'Merchant unavailable',
  duplicate_order: 'Duplicate order',
  date_unavailable: 'Date unavailable',
  order_error: 'Order error',
  partial_cancellation: 'Partial cancellation',
  other: 'Other',
};

const humanizeStatusToken = (v: string): string =>
  STATUS_COMMENT_LABELS[v] ?? v.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const formatStatusComment = (raw: string | null | undefined): string | undefined => {
  const text = (raw ?? '').trim();
  if (!text) return undefined;
  if (!text.startsWith('{')) return text;

  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(text);
  } catch {
    return text;
  }
  if (!payload || typeof payload !== 'object') return text;

  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const parts: string[] = [];
  if (str(payload.reason)) parts.push(humanizeStatusToken(str(payload.reason)));
  if (str(payload.note)) parts.push(str(payload.note));
  if (str(payload.refundDisposition)) {
    parts.push(`Refund: ${humanizeStatusToken(str(payload.refundDisposition))}`);
  }
  // Every field was null — nothing worth showing under the status badge.
  return parts.length ? parts.join(' — ') : undefined;
};

const num = (v: number | string | null | undefined): number => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

function mapListItemToOrder(item: ApiOrderListItem): CateringOrder {
  return {
    id: item.id,
    orderId: item.orderNumber ?? item.id,
    status: safeStatus(item.status),
    customer: {
      customerId: null,
      firstName: item.customer.firstName ?? '',
      lastName: item.customer.lastName ?? '',
      phone: item.customer.phone ?? '',
      email: item.customer.email ?? '',
      company: item.customer.company ?? '',
    },
    orderType: item.orderTypeId ?? safeOrderType(item.orderType),
    orderTypeName: safeOrderType(item.orderType),
    headcount: item.headcount ?? 0,
    eventName: item.eventName ?? '',
    items: [],
    packages: [],
    discount: { type: 'none', value: 0, reason: '', amount: 0 },
    fulfillment: {
      date: item.fulfillment?.date ?? '',
      time: item.fulfillment?.time ?? '',
      address: parseAddress(item.fulfillment?.address),
      instructions: '',
      contactPerson: '',
    },
    dietaryRestrictions: [],
    subtotal: 0,
    deliveryFee: 0,
    serviceCharge: 0,
    appliedCharges: [],
    taxExempt: false,
    tax: 0,
    gratuity: 0,
    total: item.total ?? 0,
    notes: [],
    statusHistory: [],
    leadId: item.leadId ?? null,
    estimateId: null,
    sourceLeadId: null,
    sourceEstimateId: null,
    invoiceId: null,
    recurringScheduleId: null,
    communicationLog: [],
    createdAt: item.createdAt ?? new Date().toISOString(),
    updatedAt: item.updatedAt ?? item.createdAt ?? new Date().toISOString(),
  };
}

function mapDetailToOrder(d: ApiOrderDetail): CateringOrder {
  const items: OrderLineItem[] = (d.items ?? []).map((li): OrderLineItem => isPackageApiLine(li.type) ? mapApiPackageOrderLine(li) : ({
    id: li.id,
    // Custom/addon lines carry a synthetic catalog id — blank it so the form treats
    // them as custom rows rather than unavailable catalog items.
    itemId: li.isCustomItem || li.isAddonItem ? '' : (li.itemId ?? ''),
    itemName: li.itemName ?? '',
    pricingType: 'fixed',
    quantity: num(li.quantity),
    unitPrice: num(li.unitPrice),
    modifiers: [],
    lineTotal: num(li.lineTotal),
    notes: li.notes ?? '',
    tierId: li.tierId ?? undefined,
    tierLabel: li.tierLabel ?? undefined,
    isAddonItem: !!li.isAddonItem,
    sortOrder: li.sortOrder,
    isTaxExempt: !!li.isTaxExempt,
  }));

  const appliedTaxes: AppliedTax[] = (d.appliedTaxes ?? [])
    .filter((t) => !t.removed)
    .map((t) => {
      const isItem = !!t.isItemTax;
      const taxfeesId = t.taxId ?? undefined;
      return {
        id: isItem ? `item:${taxfeesId}` : (taxfeesId ?? t.id),
        taxfeesId,
        name: t.name,
        type: t.type ?? 'PERCENT',
        rate: t.rate,
        exempt: false,
        source: (isItem ? 'item' : 'order_type') as AppliedTax['source'],
        isItemTax: isItem,
        itemIds: t.itemIds ?? [],
        amount: t.amount,
      };
    });

  const appliedCharges: AppliedCharge[] = (d.appliedCharges ?? []).map((c) => ({
    id: c.id,
    sourceChargeId: c.sourceChargeId ?? '',
    name: c.name ?? '',
    chargeType: (c.type ?? undefined) as 'PERCENT' | 'FLATFEE' | undefined,
    rate: c.value ?? undefined,
    chargeValue: c.value ?? undefined,
    amount: c.amount,
    taxable: c.taxable,
    removed: c.removed,
    itemIds: c.itemIds ?? [],
  }));

  const activeCharges = appliedCharges.filter((c) => !c.removed);
  const deliveryFee = activeCharges
    .filter((c) => (c.name ?? '').toLowerCase().includes('delivery'))
    .reduce((s, c) => s + c.amount, 0);
  const chargesTotal = activeCharges.reduce((s, c) => s + c.amount, 0);

  const statusHistory: StatusEntry[] = (d.statusHistory ?? []).map((s) => ({
    status: safeStatus(s.currentStatus),
    timestamp: s.createdAt ?? new Date().toISOString(),
    changedBy: s.createdBy ?? 'System',
    note: formatStatusComment(s.comments),
  }));

  return {
    id: d.id,
    orderId: d.orderNumber ?? d.id,
    status: safeStatus(d.status),
    customer: {
      customerId: d.customerId ?? null,
      firstName: d.customer.firstName ?? '',
      lastName: d.customer.lastName ?? '',
      phone: d.customer.phone ?? '',
      email: d.customer.email ?? '',
      company: d.customer.company ?? '',
      gstin: d.customer.gstin ?? undefined,
    },
    orderType: d.orderTypeId ?? safeOrderType(d.orderType),
    orderTypeName: safeOrderType(d.orderType),
    headcount: d.headcount ?? 0,
    eventName: d.eventName ?? '',
    eventType: (d.eventType ?? undefined) as CateringEventType | undefined,
    items,
    packages: [],
    discount: {
      type: (d.discount?.type as 'none' | 'percentage' | 'fixed') ?? 'none',
      value: d.discount?.value ?? 0,
      reason: d.discount?.reason ?? '',
      amount: d.discount?.amount ?? 0,
    },
    fulfillment: {
      date: d.fulfillment?.date ?? '',
      time: d.fulfillment?.time ?? '',
      dispatchTime: d.fulfillment?.dispatchTime ?? undefined,
      servingTime: d.fulfillment?.servingTime ?? undefined,
      address: parseAddress(d.fulfillment?.address),
      instructions: d.instructions ?? '',
      contactPerson: d.contactPerson ?? '',
    },
    dietaryRestrictions: d.dietaryRestrictions ?? [],
    contractTerms: d.contractTerms ?? '',
    subtotal: d.subtotal ?? 0,
    deliveryFee,
    serviceCharge: chargesTotal - deliveryFee,
    appliedCharges,
    appliedTaxes,
    taxExempt: false,
    tax: d.taxAmount ?? 0,
    gratuity: 0,
    total: d.total ?? 0,
    notes: [],
    statusHistory,
    leadId: d.leadId ?? null,
    leadName: d.leadName ?? null,
    estimateId: d.estimateId ?? null,
    sourceLeadId: null,
    sourceEstimateId: null,
    internalNotes: d.internalNotes ?? undefined,
    documentProfile: d.documentProfile ?? undefined,
    invoiceId: d.invoice?.invoiceId ?? null,
    invoiceStatus: d.invoice?.status ?? null,
    recurringScheduleId: null,
    cancellationReason: (d.cancellation?.reason ?? undefined) as CancellationReason | undefined,
    cancellationNotes: d.cancellation?.note ?? undefined,
    communicationLog: (d.communicationLog ?? []).map((c) => ({
      id: c.id,
      channel: String(c.channel ?? 'email').toLowerCase() as 'email' | 'sms',
      templateKey: 'order_confirmation',
      recipientEmail: c.recipientEmail ?? undefined,
      recipientPhone: c.recipientPhone ?? undefined,
      subject: c.subject ?? undefined,
      body: c.body ?? '',
      sentAt: c.sentAt ?? new Date().toISOString(),
      sentBy: c.sentBy ?? 'System',
      result: c.result ?? 'sent',
      reason: c.reason ?? undefined,
      note: c.note ?? undefined,
    })),
    createdAt: d.createdAt ?? new Date().toISOString(),
    updatedAt: d.updatedAt ?? new Date().toISOString(),
  };
}

/**
 * Exactly the keys the backend accepts on `items[]`. The map callback below is annotated
 * with this on purpose: a contextual return type makes excess-property checking fire on the
 * object literal, so an FE-internal field (`inDefaultTaxBase`) is a compile error rather
 * than a silent leak onto the wire. Without the annotation `.map()` just infers a wider type
 * and nothing complains.
 */
type OrderWireLineItem = ApiOrderWireItem & { sortOrder: number };

interface PreparedOrderLines {
  /** Post-sort source rows, index-aligned with `items`. Carries FE-internal flags
   *  (`inDefaultTaxBase`) that must never reach the wire. */
  source: Array<OrderPayloadLineItem & { isAddonItem?: boolean }>;
  /** Exactly what is sent as `items[]`. */
  items: OrderWireLineItem[];
}

function prepareOrderLines(items: OrderPayloadLineItem[] | undefined): PreparedOrderLines {
  // Defensive re-sort: callers are expected to pre-sort (OrderFormPage does), but this
  // guarantees addon-last ordering even if a future payload builder forgets — mirroring
  // how the estimate workers already guarantee it at this layer.
  const source = sortLineItemsAddonLast(
    (items ?? []).map((li) => ({ ...li, isAddonItem: li.isAddon })),
  );
  return {
    source,
    // Explicit whitelist (inside `toOrderWireItem`) — this is what keeps `inDefaultTaxBase` (and anything
    // else the form hangs off a line) out of the request body. Do not switch to a `...li` spread.
    // `sortOrder` is derived from the post-sort position so it always matches the array we send.
    items: source.map((li, idx): OrderWireLineItem => toOrderWireItem(li, idx)),
  };
}

function buildOrderCharges(charges: OrderChargePayload[] | undefined, lines: PreparedOrderLines): OrderChargePayload[] {
  // The charge base is the full discounted subtotal, so a charge covers every sent line.
  const scope = itemScopeFields(buildItemScope(lines.items, COVERS_ALL));
  return (charges ?? [])
    .filter((c) => !!c.sourceChargeId && (c.value ?? 0) !== 0)
    .map((c) => ({
      sourceChargeId: c.sourceChargeId,
      name: c.name,
      ...(c.type ? { type: c.type } : {}),
      ...(c.value !== undefined ? { value: c.value } : {}),
      amount: c.amount,
      taxable: c.taxable,
      ...scope,
    }));
}

function buildOrderTaxes(taxes: OrderTaxPayload[] | undefined, lines: PreparedOrderLines): OrderTaxPayload[] {
  // Order-level taxes cover the lines behind `defaultTaxableSubtotal`. The flag lives on the
  // index-aligned source rows, not on the sent objects (which are whitelisted). A missing
  // flag falls back to "covered", matching the pre-contract backend behaviour.
  const defaultTaxScope = itemScopeFields(
    buildItemScope(lines.items, (_li, i) => lines.source[i]?.inDefaultTaxBase !== false),
  );
  return (taxes ?? [])
    .map((t) => t as OrderTaxPayload & { id?: string; rate?: number; exempt?: boolean; isItemTax?: boolean; itemIds?: string[] })
    .filter((t) => !t.exempt && !!(t.taxfeesId ?? t.id))
    .map((t) => {
      const value = t.value ?? t.rate;
      const rawId = t.taxfeesId ?? t.id ?? '';
      const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : rawId;
      return {
        taxfeesId,
        name: t.name,
        ...(t.type ? { type: t.type } : {}),
        ...(value !== undefined ? { value } : {}),
        amount: t.amount,
        isTaxable: t.isTaxable,
        isItemTax: t.isItemTax ?? false,
        // Item taxes keep their resolved ids from deriveItemTaxes; order-level taxes get the
        // default tax-base scope, never their read-back `itemIds` (which would silently
        // narrow coverage to whatever was stored last time).
        ...(t.isItemTax
          ? (t.itemIds?.length ? { itemIds: t.itemIds } : {})
          : defaultTaxScope),
      };
    });
}

let lastOrdersListPayload: ReturnType<typeof A.fetchOrdersRequest>['payload'] = {};

function* fetchOrdersWorker(action: ReturnType<typeof A.fetchOrdersRequest>): SagaIterator {
  try {
    lastOrdersListPayload = action.payload;
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchOrdersFailure('No locationId available'));
      return;
    }
    const filters = { ...action.payload.filters ?? {} };
    const response: Awaited<ReturnType<typeof listOrdersApi>> = yield call(
      listOrdersApi,
      locationId,
      resolvedToken,
      filters,
    );
    const orders = (response?.data ?? []).map(mapListItemToOrder);
    yield put(A.fetchOrdersSuccess(orders, locationId, response?.total ?? orders.length, response?.page));
  } catch (e) {
    yield put(A.fetchOrdersFailure(e instanceof Error ? e.message : 'Failed to fetch orders'));
  }
}

function* fetchOrderDetailWorker(action: ReturnType<typeof A.fetchOrderDetailRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) {
      yield put(A.fetchOrderDetailFailure('No locationId available'));
      return;
    }
    const detail: Awaited<ReturnType<typeof getOrderDetailApi>> = yield call(
      getOrderDetailApi,
      action.payload.orderId,
      locationId,
      token,
    );
    yield put(A.fetchOrderDetailSuccess(mapDetailToOrder(detail)));
  } catch (e) {
    yield put(A.fetchOrderDetailFailure(e instanceof Error ? e.message : 'Failed to fetch order'));
  }
}

function* createOrderWorker(action: ReturnType<typeof A.createOrderRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token, staffId } = getCredentials();
    const d = action.payload.data;
    const locationId = (d as any).locationId || savedLocationId;

    // Built once: charges/taxes express their item coverage as positions in this exact array.
    const lines = prepareOrderLines(d.items);

    const detail: Awaited<ReturnType<typeof createOrderApi>> = yield call(
      createOrderApi,
      {
        locationId,
        ...(staffId ? { staffId } : {}),
        ...(d.leadId ? { leadId: d.leadId } : {}),
        ...(d.estimateId ? { estimateId: d.estimateId } : {}),
        ...(d.customer ? {
          customer: {
            firstName: d.customer.firstName,
            lastName: d.customer.lastName,
            phone: d.customer.phone || undefined,
            email: d.customer.email || undefined,
            company: d.customer.company || undefined,
            // Left unset (not '') when blank so the backend's estimate-gstin carry-over applies.
            gstin: d.customer.gstin || undefined,
          },
        } : {}),
        orderTypeId: d.orderTypeId,
        orderChannel: d.orderChannel || undefined,
        ...(d.eventName ? { eventName: d.eventName } : {}),
        ...(d.eventType ? { eventType: d.eventType } : {}),
        fulfillment: {
          date: d.fulfillment.date,
          // Optional — omitted rather than sent as '' when no event time was picked.
          time: d.fulfillment.time?.trim() || undefined,
          dispatchTime: d.fulfillment.dispatchTime || undefined,
          servingTime: d.fulfillment.servingTime || undefined,
          address: trimAddress(d.fulfillment.address),
        },
        ...(d.headcount ? { headcount: d.headcount } : {}),
        items: lines.items,
        appliedCharges: buildOrderCharges(d.appliedCharges, lines),
        appliedTaxes: buildOrderTaxes(d.appliedTaxes, lines),
        ...(d.discount?.type !== 'none' ? {
          discount: {
            type: d.discount.type as 'none' | 'percentage' | 'fixed',
            value: d.discount.value || undefined,
            reason: d.discount.reason?.trim() || undefined,
          },
        } : {}),
        ...(d.instructions?.trim() ? { instructions: d.instructions } : {}),
        ...(d.contractTerms?.trim() ? { contractTerms: d.contractTerms } : {}),
        ...(d.contactPerson?.trim() ? { contactPerson: d.contactPerson } : {}),
        ...(d.internalNotes?.trim() ? { internalNotes: d.internalNotes } : {}),
        status: d.status || undefined,
        ...(d.dietaryRestrictions !== undefined ? { dietaryRestrictions: d.dietaryRestrictions } : {}),
      },
      token,
    );
    yield put(A.createOrderSuccess(mapDetailToOrder(detail)));
  } catch (e) {
    yield put(A.createOrderFailure(e instanceof Error ? e.message : 'Failed to create order'));
  }
}

function* updateOrderWorker(action: ReturnType<typeof A.updateOrderRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token, staffId } = getCredentials();
    const d = action.payload.data;
    const locationId = action.payload.locationId || (d as any).locationId || savedLocationId;

    const lines = prepareOrderLines(d.items);
    // Item coverage can only reference the items[] of THIS request, so a PATCH that changes
    // charges/taxes without resending items would clear their coverage server-side. Every
    // updateOrder call site sends the full buildOrderData() result, so this is a guard.
    if (process.env.NODE_ENV !== 'production'
      && d.items === undefined
      && (d.appliedCharges !== undefined || d.appliedTaxes !== undefined)) {
      console.warn('[catering] updateOrder: appliedCharges/appliedTaxes sent without items — item coverage will be cleared.');
    }

    const response: Awaited<ReturnType<typeof updateOrderApi>> = yield call(
      updateOrderApi,
      action.payload.orderId,
      {
        locationId,
        ...(staffId ? { staffId } : {}),
        ...(d.customer ? {
          customer: {
            firstName: d.customer.firstName,
            lastName: d.customer.lastName,
            phone: d.customer.phone || undefined,
            email: d.customer.email || undefined,
            company: d.customer.company || undefined,
            gstin: d.customer.gstin || undefined,
          },
        } : {}),
        ...(d.orderTypeId ? { orderTypeId: d.orderTypeId } : {}),
        ...(d.headcount !== undefined ? { headcount: d.headcount } : {}),
        ...(d.eventName !== undefined ? { eventName: d.eventName } : {}),
        ...(d.eventType !== undefined ? { eventType: d.eventType } : {}),
        ...(d.fulfillment ? {
          fulfillment: {
            date: d.fulfillment.date || undefined,
            // Always sent (as null when cleared) — PATCH treats an omitted key as
            // "leave unchanged", so `|| undefined` would make clearing the time a no-op.
            time: d.fulfillment.time?.trim() || null,
            dispatchTime: d.fulfillment.dispatchTime || undefined,
            servingTime: d.fulfillment.servingTime || undefined,
            address: trimAddress(d.fulfillment.address),
          },
        } : {}),
        ...(d.items !== undefined ? { items: lines.items } : {}),
        ...(d.appliedCharges !== undefined ? { appliedCharges: buildOrderCharges(d.appliedCharges, lines) } : {}),
        ...(d.appliedTaxes !== undefined ? { appliedTaxes: buildOrderTaxes(d.appliedTaxes, lines) } : {}),
        ...(d.dietaryRestrictions !== undefined ? { dietaryRestrictions: d.dietaryRestrictions } : {}),
        ...(d.discount ? {
          discount: {
            type: d.discount.type as 'none' | 'percentage' | 'fixed',
            value: d.discount.value || undefined,
            reason: d.discount.reason?.trim() || undefined,
          },
        } : {}),
        ...(d.instructions?.trim() ? { instructions: d.instructions } : {}),
        // PATCH semantics: an omitted key means "leave unchanged" server-side, not "clear
        // it" — so Contract Terms and Internal Notes must always be sent on update, even
        // as '', or clearing them in the form silently fails to persist.
        contractTerms: d.contractTerms ?? '',
        internalNotes: d.internalNotes ?? '',
        ...(d.contactPerson?.trim() ? { contactPerson: d.contactPerson } : {}),
        status: d.status || undefined,
      },
      token,
      action.payload.force ?? false,
    );
    if ((response as any)?.warning?.requiresConfirmation) {
      yield put(A.updateOrderWarning((response as any).warning));
      return;
    }
    yield put(A.updateOrderSuccess(mapDetailToOrder(response as ApiOrderDetail)));
  } catch (e) {
    yield put(A.updateOrderFailure(e instanceof Error ? e.message : 'Failed to update order'));
  }
}

function* orderActionWorker(action: ReturnType<typeof A.orderActionRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token, staffId } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    let body: OrderActionBody;
    if (action.payload.action === 'reschedule') {
      body = {
        action: 'reschedule',
        locationId,
        fulfillment: action.payload.fulfillment,
        ...(action.payload.notifyCustomer != null ? { notifyCustomer: action.payload.notifyCustomer } : {}),
        ...(action.payload.leadTimeOverride ? { leadTimeOverride: true } : {}),
        ...(staffId ? { staffId } : {}),
      };
    } else {
      body = {
        action: 'cancel',
        locationId,
        reason: action.payload.reason as any,
        ...(action.payload.note ? { note: action.payload.note } : {}),
        ...(action.payload.refundDisposition ? { refundDisposition: action.payload.refundDisposition } : {}),
        ...(action.payload.notifyCustomer != null ? { notifyCustomer: action.payload.notifyCustomer } : {}),
        ...(staffId ? { staffId } : {}),
      };
    }
    const detail: Awaited<ReturnType<typeof orderActionApi>> = yield call(
      orderActionApi,
      action.payload.orderId,
      body,
      token,
    );
    yield put(A.orderActionSuccess(mapDetailToOrder(detail)));
  } catch (e) {
    yield put(A.orderActionFailure(e instanceof Error ? e.message : 'Failed to perform action'));
  }
}

function* transitionOrderStatusWorker(action: ReturnType<typeof A.transitionOrderStatusRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token, staffId } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;

    let backendStatus: 'confirmed' | 'preparing' | 'completed';
    if (action.payload.status === 'confirmed') backendStatus = 'confirmed';
    else if (action.payload.status === 'completed') backendStatus = 'completed';
    else if (action.payload.status === 'in_prep' || (action.payload.status as string) === 'preparing') backendStatus = 'preparing';
    else {
      yield put(A.transitionOrderStatusFailure(`Status '${action.payload.status}' is not supported by the API.`));
      return;
    }

    yield call(
      transitionOrderStatusApi,
      action.payload.orderId,
      { locationId, status: backendStatus, ...(staffId ? { staffId } : {}) },
      token,
    );
    const feStatus: OrderStatus = backendStatus === 'preparing' ? 'in_prep' : backendStatus;
    yield put(A.transitionOrderStatusSuccess(action.payload.orderId, feStatus));
    // No caching: reload the authoritative record (status history) from the API.
    yield put(A.fetchOrderDetailRequest(action.payload.orderId));
  } catch (e) {
    yield put(A.transitionOrderStatusFailure(e instanceof Error ? e.message : 'Failed to transition order status'));
  }
}

function* sendOrderWorker(action: ReturnType<typeof A.sendOrderRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    const { orderId, data } = action.payload;
    const channels = data.channels?.length ? data.channels : (['email'] as const);
    let response!: Awaited<ReturnType<typeof sendOrderApi>>;
    for (const channel of channels) {
      const channelBody = channel === 'sms' ? (data.smsBodyOverride ?? data.bodyOverride) : data.bodyOverride;
      response = yield call(
        sendOrderApi,
        orderId,
        {
          locationId,
          channels: [channel],
          ...(channel === 'email' && data.subjectOverride ? { subjectOverride: data.subjectOverride } : {}),
          ...(channelBody ? { bodyOverride: channelBody } : {}),
          ...(channel === 'sms' && data.phoneOverride ? { phoneOverride: data.phoneOverride } : {}),
        },
        token,
      );
    }
    yield put(A.sendOrderSuccess(response.orderId, response.sentAt));
    yield put(A.fetchOrderDetailRequest(orderId));
  } catch (e) {
    yield put(A.sendOrderFailure(e instanceof Error ? e.message : 'Failed to send order confirmation'));
  }
}

function* fetchOrderDayCapacityWorker(action: ReturnType<typeof A.fetchOrderDayCapacityRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.fetchOrderDayCapacityFailure('No locationId available'));
      return;
    }
    const { date, excludeOrderId } = action.payload;
    const capacity: Awaited<ReturnType<typeof getOrderDayCapacityApi>> = yield call(
      getOrderDayCapacityApi, locationId, date, token, excludeOrderId,
    );
    yield put(A.fetchOrderDayCapacitySuccess(capacity));
  } catch (e) {
    yield put(A.fetchOrderDayCapacityFailure(e instanceof Error ? e.message : 'Failed to fetch day capacity'));
  }
}

function* refetchOrdersListAfterMutation(): SagaIterator {
  yield put(A.fetchOrdersRequest(lastOrdersListPayload));
}

function* cateringOrdersSaga(): SagaIterator {
  yield all([
    takeLatest(CO.FETCH_REQUEST, fetchOrdersWorker),
    takeLatest(CO.FETCH_DETAIL_REQUEST, fetchOrderDetailWorker),
    takeEvery(CO.CREATE_REQUEST, createOrderWorker),
    takeEvery(CO.UPDATE_REQUEST, updateOrderWorker),
    takeEvery(CO.ACTION_REQUEST, orderActionWorker),
    takeEvery(CO.STATUS_REQUEST, transitionOrderStatusWorker),
    takeEvery(CO.SEND_REQUEST, sendOrderWorker),
    takeLatest(CO.DAY_CAPACITY_REQUEST, fetchOrderDayCapacityWorker),
    takeLatest(
      [CO.CREATE_SUCCESS, CO.UPDATE_SUCCESS, CO.ACTION_SUCCESS, CO.STATUS_SUCCESS],
      refetchOrdersListAfterMutation,
    ),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// CUSTOMERS SAGA
// ════════════════════════════════════════════════════════════════════════════════

function mapApiCustomerToCustomer(item: ApiCustomer): CateringCustomer {
  return {
    id: item.id,
    firstName: item.firstName ?? '',
    lastName: item.lastName ?? '',
    email: item.email ?? '',
    phone: item.phone ?? '',
    company: item.company ?? '',
    tags: item.tags ?? [],
    dietaryNotes: item.dietaryNotes ?? '',
    totalOrders: item.totalOrders ?? 0,
    totalRevenue: item.totalRevenue ?? 0,
    avgOrderValue: item.avgOrderValue ?? 0,
    firstOrderDate: item.firstOrderDate ?? null,
    lastOrderDate: item.lastOrderDate ?? null,
    favoriteItems: item.favoriteItems ?? [],
    createdAt: item.createdAt ?? new Date().toISOString(),
    updatedAt: item.updatedAt ?? new Date().toISOString(),
  };
}

let lastCustomersListPayload: ReturnType<typeof A.fetchCustomersRequest>['payload'] = {};

function* fetchCustomersWorker(action: ReturnType<typeof A.fetchCustomersRequest>): SagaIterator {
  try {
    lastCustomersListPayload = action.payload;
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchCustomersFailure('No locationId available'));
      return;
    }
    const response: Awaited<ReturnType<typeof listCustomersApi>> = yield call(
      listCustomersApi,
      locationId,
      resolvedToken,
      action.payload.filters ?? {},
    );
    const customers = (response?.data ?? []).map(mapApiCustomerToCustomer);
    yield put(A.fetchCustomersSuccess(customers, response?.total ?? customers.length, response?.page));
  } catch (e) {
    yield put(A.fetchCustomersFailure(e instanceof Error ? e.message : 'Failed to fetch customers'));
  }
}

function* fetchCustomerDetailWorker(action: ReturnType<typeof A.fetchCustomerDetailRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) {
      yield put(A.fetchCustomerDetailFailure('No locationId available'));
      return;
    }
    const detail: Awaited<ReturnType<typeof getCustomerDetailApi>> = yield call(
      getCustomerDetailApi,
      action.payload.customerId,
      locationId,
      token,
    );
    yield put(A.fetchCustomerDetailSuccess(mapApiCustomerToCustomer(detail)));
  } catch (e) {
    yield put(A.fetchCustomerDetailFailure(e instanceof Error ? e.message : 'Failed to fetch customer'));
  }
}

function* updateCustomerWorker(action: ReturnType<typeof A.updateCustomerRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    yield call(
      updateCustomerApi,
      action.payload.customerId,
      { locationId, ...action.payload.data },
      token,
    );
    yield put(A.updateCustomerSuccess(action.payload.customerId, action.payload.data));
    yield put(A.fetchCustomerDetailRequest({ customerId: action.payload.customerId, locationId }));
  } catch (e) {
    yield put(A.updateCustomerFailure(e instanceof Error ? e.message : 'Failed to update customer'));
  }
}

function* deleteCustomerWorker(action: ReturnType<typeof A.deleteCustomerRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    yield call(
      deleteCustomerApi,
      action.payload.customerId,
      locationId,
      token,
    );
    yield put(A.deleteCustomerSuccess(action.payload.customerId));
  } catch (e) {
    yield put(A.deleteCustomerFailure(e instanceof Error ? e.message : 'Failed to delete customer'));
  }
}

function* refetchCustomersListAfterMutation(): SagaIterator {
  yield put(A.fetchCustomersRequest(lastCustomersListPayload));
}

function* cateringCustomersSaga(): SagaIterator {
  yield all([
    takeLatest(CC.FETCH_REQUEST, fetchCustomersWorker),
    takeLatest(CC.FETCH_DETAIL_REQUEST, fetchCustomerDetailWorker),
    takeEvery(CC.UPDATE_REQUEST, updateCustomerWorker),
    takeEvery(CC.DELETE_REQUEST, deleteCustomerWorker),
    takeLatest(
      [CC.UPDATE_SUCCESS, CC.DELETE_SUCCESS],
      refetchCustomersListAfterMutation,
    ),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// INVOICES SAGA
// ════════════════════════════════════════════════════════════════════════════════

function mapListItemToInvoice(item: ApiInvoiceListItem): CateringInvoice {
  return {
    id: item.id,
    invoiceNumber: item.invoiceNumber,
    orderId: item.orderId,
    orderNumber: item.orderNumber ?? null,
    status: (item.status ?? 'draft') as CateringInvoice['status'],
    customerEmail: item.customerEmail ?? '',
    customerPhone: item.customerPhone ?? '',
    lineItems: [],
    appliedCharges: [],
    lineItemsTotal: parseFloat(item.lineItemsTotal) || 0,
    discountAmount: parseFloat(item.discountAmount) || 0,
    subtotal: parseFloat(item.subtotal) || 0,
    deliveryFee: parseFloat(item.deliveryFee) || 0,
    serviceCharge: parseFloat(item.serviceCharge) || 0,
    tax: parseFloat(item.tax) || 0,
    total: parseFloat(item.total) || 0,
    notes: '',
    paymentSchedule: [],
    amountPaid: parseFloat(item.amountPaid) || 0,
    outstandingBalance: parseFloat(item.outstandingBalance) || 0,
    payments: [],
    sentAt: item.sentAt ?? null,
    nextDueDate: item.nextDueDate ?? null,
    nextDueAmount: item.nextDueAmount != null ? parseFloat(item.nextDueAmount) : null,
    communicationLog: [],
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

function mapDetailToInvoice(d: ApiInvoiceDetail): CateringInvoice {
  return {
    id: d.id,
    invoiceNumber: d.invoiceNumber,
    orderId: d.orderId,
    orderNumber: d.orderNumber ?? null,
    status: (d.status ?? 'draft') as CateringInvoice['status'],
    customerEmail: d.customerEmail ?? '',
    customerProfileEmail: d.customerProfileEmail ?? '',
    customerPhone: d.customerPhone ?? '',
    lineItems: (d.lineItems ?? []).map((li) => ({
      id: li.id,
      name: li.name || undefined,
      description: li.description,
      quantity: li.quantity,
      unitPrice: parseFloat(li.unitPrice) || 0,
      lineTotal: parseFloat(li.lineTotal) || 0,
      itemId: li.itemId || undefined,
      tierId: li.tierId ?? undefined,
      tierLabel: li.tierLabel ?? undefined,
      isAddonItem: !!li.isAddonItem,
      sortOrder: li.sortOrder,
      isTaxExempt: !!li.isTaxExempt,
      ...(isPackageApiLine(li.type)
        ? {
          type: 'package' as const,
          packageVariantId: li.packageVariantId ?? null,
          packageVariantName: li.packageVariantName ?? null,
          packageTagIds: li.packageTagIds ?? [],
          packageServes: li.servesCount ?? null,
          packageItems: normalizePackageItems(li.packageItems),
        }
        : {}),
    })),
    appliedCharges: (d.appliedCharges ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      type: c.type ?? null,
      rate: c.rate ?? null,
      amount: c.amount ?? null,
      taxable: c.taxable,
    })),
    // Itemized breakdown (SGST/CGST/IGST etc.). Left undefined when the response omits
    // the key, and legitimately `[]` for invoices billed with the simple taxRate — so
    // `tax` below stays the source of truth for the total either way.
    ...(d.appliedTaxes ? {
      appliedTaxes: d.appliedTaxes.map((t) => {
        const isItem = !!t.isItemTax;
        const taxfeesId = t.taxfeesId ?? undefined;
        return {
          id: t.id ?? (isItem ? `item:${taxfeesId}` : taxfeesId) ?? t.name,
          taxfeesId,
          name: t.name,
          type: (t.type ?? 'PERCENT') as 'PERCENT' | 'FLATFEE',
          // Money/rate fields arrive as strings on this endpoint like every sibling below,
          // so coerce here — a raw "1260.00" compares unequal to 1260 and would defeat any
          // downstream grouping (see dedupeIdenticalTaxes) while rendering identically.
          rate: num(t.rate),
          exempt: t.exempt ?? false,
          source: (isItem ? 'item' : 'order_type') as AppliedTax['source'],
          isItemTax: isItem,
          isTaxable: t.isTaxable,
          amount: t.amount == null ? undefined : num(t.amount),
          itemIds: t.itemIds ?? [],
        };
      }),
    } : {}),
    lineItemsTotal: parseFloat(d.lineItemsTotal) || 0,
    discountAmount: parseFloat(d.discountAmount) || 0,
    subtotal: parseFloat(d.subtotal) || 0,
    deliveryFee: parseFloat(d.deliveryFee) || 0,
    serviceCharge: parseFloat(d.serviceCharge) || 0,
    tax: parseFloat(d.tax) || 0,
    total: parseFloat(d.total) || 0,
    notes: d.notes ?? '',
    paymentSchedule: (d.paymentSchedule ?? []).map((ms) => ({
      id: ms.id,
      label: ms.label,
      amount: parseFloat(ms.amount) || 0,
      dueDate: ms.dueDate,
      status: (ms.status?.toLowerCase() ?? 'pending') as any,
      paidAt: ms.paidAt ?? undefined,
      paymentId: ms.paymentId ?? undefined,
      amountPaid: ms.amountPaid != null ? (parseFloat(ms.amountPaid) || 0) : undefined,
      balance: ms.balance != null ? (parseFloat(ms.balance) || 0) : undefined,
    })),
    amountPaid: parseFloat(d.amountPaid) || 0,
    outstandingBalance: parseFloat(d.outstandingBalance) || 0,
    payments: d.payments ?? [],
    sentAt: d.sentAt ?? null,
    nextDueDate: null,
    nextDueAmount: null,
    communicationLog: (d.communicationLog ?? []).map((cl) => ({
      id: cl.id,
      channel: String(cl.channel ?? 'email').toLowerCase() as 'email' | 'sms',
      templateKey: (cl.templateKey ?? 'invoice_sent') as any,
      recipientEmail: cl.recipientEmail ?? undefined,
      recipientPhone: cl.recipientPhone ?? undefined,
      subject: cl.subject ?? undefined,
      body: cl.body ?? '',
      sentAt: cl.sentAt,
      sentBy: cl.sentBy ?? '',
      result: cl.result ?? 'sent',
      reason: cl.reason ?? undefined,
      note: cl.note ?? undefined,
    })),
    createdAt: d.createdAt,
    updatedAt: d.updatedAt ?? d.createdAt,
  };
}

let lastInvoicesListPayload: ReturnType<typeof A.fetchInvoicesRequest>['payload'] = {};

function* fetchInvoicesWorker(action: ReturnType<typeof A.fetchInvoicesRequest>): SagaIterator {
  try {
    lastInvoicesListPayload = action.payload;
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const resolvedToken = action.payload.token ?? token;
    if (!locationId) {
      yield put(A.fetchInvoicesFailure('No locationId available'));
      return;
    }
    const { status, search, from, to, page, limit, sort, order } = action.payload;
    const response: Awaited<ReturnType<typeof listInvoicesApi>> = yield call(
      listInvoicesApi,
      locationId,
      resolvedToken,
      { status, search, from, to, page, limit, sort, order },
    );
    const invoices = (response?.data ?? []).map(mapListItemToInvoice);
    yield put(A.fetchInvoicesSuccess(invoices, locationId, response?.page));
  } catch (e) {
    yield put(A.fetchInvoicesFailure(e instanceof Error ? e.message : 'Failed to fetch invoices'));
  }
}

function* createInvoiceWorker(action: ReturnType<typeof A.createInvoiceRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.createInvoiceFailure('No locationId available'));
      return;
    }
    const { data } = action.payload;

    const activeCharges = data.appliedCharges
      .filter((c) => !c.removed && (c.rate ?? c.chargeValue ?? 0) !== 0)
      .map((c) => ({
        name: c.name,
        type: (c.chargeType ?? 'FLATFEE') as 'FLATFEE' | 'PERCENT',
        rate: c.rate ?? c.chargeValue ?? 0,
        amount: c.amount,
        taxable: c.taxable,
      }));

    const activeTaxes = (data.appliedTaxes ?? []).map((t) => {
      const rawId = t.taxfeesId ?? t.id ?? '';
      const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
      return {
        ...(taxfeesId && !taxfeesId.startsWith('ac-') ? { taxfeesId } : {}),
        name: t.name,
        type: t.type,
        rate: t.rate,
        amount: t.amount ?? 0,
        exempt: t.exempt,
        isTaxable: t.isTaxable,
      };
    });

    const created: Awaited<ReturnType<typeof createInvoiceApi>> = yield call(
      createInvoiceApi,
      {
        locationId,
        orderId: data.orderId,
        customerEmail: data.customerEmail || undefined,
        lineItems: data.lineItems.map((li) => ({
          name: li.name || undefined,
          description: li.description,
          quantity: li.quantity,
          unitPrice: li.unitPrice,
          itemId: li.itemId || undefined,
        })),
        appliedCharges: activeCharges.length > 0 ? activeCharges : undefined,
        appliedTaxes: activeTaxes.length > 0 ? activeTaxes : undefined,
        taxRate: data.taxRate || undefined,
        taxExempt: data.taxExempt || undefined,
        gratuity: data.gratuity || undefined,
        discountAmount: data.discountAmount || undefined,
        paymentSchedule: data.paymentSchedule.map((ms) => ({
          label: ms.label || undefined,
          amount: parseFloat(ms.amount) || 0,
          dueDate: ms.dueDate,
        })),
        notes: data.notes || undefined,
        sendImmediately: data.sendImmediately,
        channels: data.channels,
        phoneOverride: data.phoneOverride || undefined,
        ...(staffId ? { staffId } : {}),
      },
      token,
    );

    const now = created.createdAt ?? new Date().toISOString();
    const createSent = data.sendImmediately ? (created.sent ?? created.status !== 'draft') : false;
    if (data.sendImmediately) {
      yield put(A.notifyInvoiceSuccess(created.invoiceId, {
        sent: createSent,
        skipped: created.skipped ?? [],
        status: created.status ?? 'draft',
        issuedAt: createSent ? (created.issuedAt ?? now) : null,
      }));
    }
    const newInvoice: CateringInvoice = {
      id: created.invoiceId,
      invoiceNumber: created.invoiceNumber,
      orderId: data.orderId,
      // So the new row in the list reads as the order NUMBER from the first frame (not the raw order id, which would
      // show until the list refetch replaces this optimistic row).
      orderNumber: data.orderNumber ?? null,
      status: (created.status ?? 'draft') as CateringInvoice['status'],
      customerEmail: data.customerEmail,
      lineItems: data.lineItems.map((li, idx) => ({
        id: `li-${idx}`,
        name: li.name || undefined,
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        lineTotal: li.quantity * li.unitPrice,
        itemId: li.itemId || undefined,
      })),
      appliedCharges: [],
      lineItemsTotal: (data.subtotal ?? 0) + (data.discountAmount ?? 0),
      discountAmount: data.discountAmount ?? 0,
      subtotal: data.subtotal,
      deliveryFee: data.deliveryFee,
      serviceCharge: data.serviceCharge,
      tax: data.tax,
      total: parseFloat(created.total) || data.total,
      notes: data.notes ?? '',
      paymentSchedule: data.paymentSchedule.map((ms, idx) => ({
        id: `ms-${idx}`,
        label: ms.label,
        amount: parseFloat(ms.amount) || 0,
        dueDate: ms.dueDate,
        status: 'pending' as const,
      })),
      amountPaid: 0,
      outstandingBalance: parseFloat(created.total) || data.total,
      payments: [],
      sentAt: createSent ? (created.issuedAt ?? now) : null,
      nextDueDate: data.paymentSchedule?.[0]?.dueDate ?? null,
      nextDueAmount: data.paymentSchedule?.[0] ? parseFloat(data.paymentSchedule[0].amount) || null : null,
      communicationLog: [],
      createdAt: now,
      updatedAt: now,
    };

    yield put(A.createInvoiceSuccess(newInvoice));
    yield put(A.fetchInvoiceDetailRequest(created.invoiceId));
  } catch (e) {
    yield put(A.createInvoiceFailure(e instanceof Error ? e.message : 'Failed to create invoice'));
  }
}

function* updateInvoiceWorker(action: ReturnType<typeof A.updateInvoiceRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.updateInvoiceFailure('No locationId available'));
      return;
    }
    const { id, data } = action.payload;

    const activeCharges = (data.appliedCharges ?? [])
      .filter((c) => !c.removed && (c.rate ?? c.chargeValue ?? 0) !== 0)
      .map((c) => ({
        name: c.name,
        type: (c.chargeType ?? 'FLATFEE') as 'FLATFEE' | 'PERCENT',
        rate: c.rate ?? c.chargeValue ?? 0,
        taxable: c.taxable,
      }));

    const activeTaxes = (data.appliedTaxes ?? []).map((t) => {
      const rawId = t.taxfeesId ?? t.id ?? '';
      const taxfeesId = rawId.startsWith('item:') ? rawId.slice(5) : (rawId || undefined);
      return {
        ...(taxfeesId && !taxfeesId.startsWith('ac-') ? { taxfeesId } : {}),
        name: t.name,
        type: t.type,
        rate: t.rate,
        amount: t.amount ?? 0,
        exempt: t.exempt,
        isTaxable: t.isTaxable,
      };
    });

    const response: Awaited<ReturnType<typeof updateInvoiceApi>> = yield call(
      updateInvoiceApi,
      id,
      {
        locationId,
        customerEmail: data.customerEmail || undefined,
        lineItems: data.lineItems?.map((li) => ({
          name: li.name || undefined,
          description: li.description,
          quantity: li.quantity,
          unitPrice: li.unitPrice,
          itemId: li.itemId || undefined,
        })),
        appliedCharges: data.appliedCharges !== undefined ? activeCharges : undefined,
        appliedTaxes: data.appliedTaxes !== undefined ? activeTaxes : undefined,
        taxRate: data.taxRate || undefined,
        taxExempt: data.taxExempt || undefined,
        gratuity: data.gratuity || undefined,
        paymentSchedule: data.paymentSchedule?.map((ms) => ({
          label: ms.label || undefined,
          amount: parseFloat(ms.amount) || 0,
          dueDate: ms.dueDate,
        })),
        notes: data.notes || undefined,
        sendImmediately: data.sendImmediately,
        ...(data.sendImmediately ? { channels: data.channels, phoneOverride: data.phoneOverride || undefined } : {}),
        ...(staffId ? { staffId } : {}),
      },
      token,
    );

    // With sendImmediately the invoice may come back as a draft (nothing could be delivered).
    const updateSent = data.sendImmediately ? (response.sent ?? response.status !== 'draft') : false;
    if (data.sendImmediately) {
      yield put(A.notifyInvoiceSuccess(id, {
        sent: updateSent,
        skipped: response.skipped ?? [],
        status: response.status ?? 'draft',
        issuedAt: updateSent ? (response.issuedAt ?? response.updatedAt ?? new Date().toISOString()) : null,
      }));
    }

    const updatedInvoice: Partial<CateringInvoice> & { id: string } = {
      id,
      status: (response.status ?? 'draft') as CateringInvoice['status'],
      updatedAt: response.updatedAt ?? new Date().toISOString(),
      ...(data.customerEmail !== undefined ? { customerEmail: data.customerEmail } : {}),
      ...(data.lineItems !== undefined ? {
        lineItems: data.lineItems.map((li, idx) => ({
          id: `li-${idx}`,
          name: li.name || undefined,
          description: li.description,
          quantity: li.quantity,
          unitPrice: li.unitPrice,
          lineTotal: li.quantity * li.unitPrice,
          itemId: li.itemId || undefined,
        })),
      } : {}),
      ...(data.subtotal !== undefined ? { subtotal: data.subtotal } : {}),
      ...(data.total !== undefined ? { total: data.total } : {}),
      ...(data.tax !== undefined ? { tax: data.tax } : {}),
      ...(data.deliveryFee !== undefined ? { deliveryFee: data.deliveryFee } : {}),
      ...(data.serviceCharge !== undefined ? { serviceCharge: data.serviceCharge } : {}),
      ...(data.notes !== undefined ? { notes: data.notes } : {}),
      ...(updateSent ? { sentAt: response.issuedAt ?? response.updatedAt ?? new Date().toISOString() } : {}),
    };

    yield put(A.updateInvoiceSuccess(updatedInvoice));
    yield put(A.fetchInvoiceDetailRequest(id));
  } catch (e) {
    yield put(A.updateInvoiceFailure(e instanceof Error ? e.message : 'Failed to update invoice'));
  }
}

function* fetchInvoiceDetailWorker(
  action: ReturnType<typeof A.fetchInvoiceDetailRequest>,
): SagaIterator {
  try {
    const { token } = getCredentials();
    const raw: ApiInvoiceDetail = yield call(getInvoiceDetailApi, action.payload.id, token);
    yield put(A.fetchInvoiceDetailSuccess(mapDetailToInvoice(raw)));
  } catch (e) {
    yield put(A.fetchInvoiceDetailFailure(e instanceof Error ? e.message : 'Failed to fetch invoice'));
  }
}

function* notifyInvoiceWorker(action: ReturnType<typeof A.notifyInvoiceRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.notifyInvoiceFailure('No locationId available'));
      return;
    }
    const { id, data } = action.payload;
    // One API call per channel — see sendEstimateWorker for why this loops here rather
    // than the caller dispatching once per channel.
    const channels = data.channels?.length ? data.channels : (['email'] as const);
    let response!: Awaited<ReturnType<typeof notifyInvoiceApi>>;
    const responses: Awaited<ReturnType<typeof notifyInvoiceApi>>[] = [];
    for (const channel of channels) {
      const channelBody = channel === 'sms' ? (data.smsBodyOverride ?? data.bodyOverride) : data.bodyOverride;
      response = yield call(
        notifyInvoiceApi,
        id,
        {
          locationId,
          channels: [channel],
          ...(channel === 'email' ? { subjectOverride: data.subjectOverride, customerEmail: data.customerEmail } : {}),
          ...(channelBody ? { bodyOverride: channelBody } : {}),
          ...(channel === 'sms' ? { phoneOverride: data.phoneOverride } : {}),
          ...(staffId ? { staffId } : {}),
        },
        token,
      );
      responses.push(response);
    }
    const outcome = mergeSendOutcomes(responses.map((r) => ({ ...r, sentAt: r.issuedAt })));
    yield put(A.notifyInvoiceSuccess(response.invoiceId, {
      sent: outcome.sent,
      skipped: outcome.skipped,
      status: outcome.status,
      issuedAt: outcome.sentAt,
    }));
    // Reload from DB so the communication log + status reflect the server, not optimistic values.
    yield put(A.fetchInvoiceDetailRequest(id));
  } catch (e) {
    yield put(A.notifyInvoiceFailure(e instanceof Error ? e.message : 'Failed to send invoice'));
  }
}

function* sendPaymentReminderWorker(action: ReturnType<typeof A.sendPaymentReminderRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.sendPaymentReminderFailure('No locationId available'));
      return;
    }
    const response: Awaited<ReturnType<typeof sendPaymentReminderApi>> = yield call(
      sendPaymentReminderApi, action.payload.id, locationId, token, staffId,
    );
    yield put(A.sendPaymentReminderSuccess(action.payload.id, {
      sent: response?.sent === true,
      skipped: response?.skipped ?? [],
    }));
    // Reload from DB so the communication log (reminder entry / skipped attempt) reflects the server.
    yield put(A.fetchInvoiceDetailRequest(action.payload.id));
  } catch (e) {
    yield put(A.sendPaymentReminderFailure(e instanceof Error ? e.message : 'Failed to send payment reminder'));
  }
}

function* statusActionWorker(action: ReturnType<typeof A.statusActionRequest>): SagaIterator {
  try {
    const { locationId, token, staffId } = getCredentials();
    if (!locationId) {
      yield put(A.statusActionFailure('No locationId available'));
      return;
    }
    const { id, action: statusAction } = action.payload;
    const response: Awaited<ReturnType<typeof updateInvoiceApi>> = yield call(
      updateInvoiceApi,
      id,
      { locationId, action: statusAction, ...(staffId ? { staffId } : {}) },
      token,
    );
    yield put(A.statusActionSuccess(response.invoiceId, response.status, response.updatedAt));
    // Reload from DB so status history + payment schedule reflect the server, not optimistic values.
    yield put(A.fetchInvoiceDetailRequest(id));
  } catch (e) {
    yield put(A.statusActionFailure(e instanceof Error ? e.message : 'Failed to update invoice status'));
  }
}

function* recordPaymentWorker(action: ReturnType<typeof A.recordPaymentRequest>): SagaIterator {
  try {
    const { locationId, token } = getCredentials();
    if (!locationId) {
      yield put(A.recordPaymentFailure('No locationId available'));
      return;
    }
    const { invoiceId, input } = action.payload;
    const response: Awaited<ReturnType<typeof recordInvoicePaymentApi>> = yield call(
      recordInvoicePaymentApi,
      invoiceId,
      {
        locationId,
        type: input.type,
        amount: input.amount,
        method: input.method,
        reference: input.reference,
        reason: input.reason,
        paymentScheduleId: input.paymentScheduleId,
        notes: input.notes,
        paymentDate: input.paymentDate,
      },
      token,
    );
    const p = response.payment;
    const payment: Payment = {
      id: p.id,
      type: p.type,
      paymentScheduleId: p.paymentScheduleId ?? null,
      amount: p.amount,
      // Stored verbatim server-side and rendered as-is — don't substitute a method it never sent.
      method: p.method ?? '',
      reference: p.reference ?? '',
      date: p.date,
      recordedBy: p.recordedBy ?? 'Staff',
      isVoided: !!p.isVoided,
      voidedAt: p.voidedAt ?? undefined,
      notes: p.notes ?? '',
    };
    yield put(A.recordPaymentSuccess(
      response.invoiceId,
      payment,
      response.status,
      parseFloat(response.amountPaid) || 0,
      parseFloat(response.outstandingBalance) || 0,
      response.updatedAt,
    ));
    // Reconcile the full detail (payment schedule milestones, status history) from server.
    yield put(A.fetchInvoiceDetailRequest(invoiceId));
  } catch (e) {
    yield put(A.recordPaymentFailure(e instanceof Error ? e.message : 'Failed to record payment'));
  }
}

function* refetchInvoicesListAfterMutation(): SagaIterator {
  yield put(A.fetchInvoicesRequest(lastInvoicesListPayload));
}

function* cateringInvoicesSaga(): SagaIterator {
  yield all([
    takeLatest(CI.FETCH_REQUEST, fetchInvoicesWorker),
    takeLatest(CI.CREATE_REQUEST, createInvoiceWorker),
    takeLatest(CI.UPDATE_REQUEST, updateInvoiceWorker),
    takeLatest(CI.FETCH_DETAIL_REQUEST, fetchInvoiceDetailWorker),
    takeLatest(CI.NOTIFY_REQUEST, notifyInvoiceWorker),
    takeLatest(CI.SEND_REMINDER_REQUEST, sendPaymentReminderWorker),
    takeLatest(CI.STATUS_ACTION_REQUEST, statusActionWorker),
    takeLatest(CI.RECORD_PAYMENT_REQUEST, recordPaymentWorker),
    takeLatest(
      [CI.CREATE_SUCCESS, CI.UPDATE_SUCCESS, CI.STATUS_ACTION_SUCCESS, CI.RECORD_PAYMENT_SUCCESS, CI.NOTIFY_SUCCESS],
      refetchInvoicesListAfterMutation,
    ),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDER TYPES SAGA
// ════════════════════════════════════════════════════════════════════════════════

function* fetchOrderTypesWorker(): SagaIterator {
  // Disabled: /api/v1/catering/order-types is not called; orderTypes is populated
  // from the settings response instead (see fetchMultipleSettingsWorker).
}

function* fetchOrderTypeFeesWorker(action: ReturnType<typeof A.fetchOrderTypeFeesRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    const { orderTypeId } = action.payload;
    if (!locationId || !orderTypeId) {
      yield put(A.fetchOrderTypeFeesFailure('No locationId or orderTypeId available'));
      return;
    }
    const fees: Awaited<ReturnType<typeof getOrderTypeFeesApi>> = yield call(
      getOrderTypeFeesApi,
      orderTypeId,
      locationId,
      token,
    );
    const mapped = (Array.isArray(fees) ? fees : []).map((f: ApiOrderTypeFee) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      rate: f.rate,
      is_taxable: f.is_taxable,
    }));
    yield put(A.fetchOrderTypeFeesSuccess(mapped, orderTypeId));
  } catch (e) {
    yield put(A.fetchOrderTypeFeesFailure(e instanceof Error ? e.message : 'Failed to fetch order type fees'));
  }
}

function* cateringOrderTypesSaga(): SagaIterator {
  yield all([
    takeLatest(COT.FETCH_REQUEST, fetchOrderTypesWorker),
    takeLatest(COT.FETCH_FEES_REQUEST, fetchOrderTypeFeesWorker),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// SETTINGS SAGA
// ════════════════════════════════════════════════════════════════════════════════

function* fetchSettingWorker(action: ReturnType<typeof A.fetchSettingRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) { yield put(A.fetchSettingFailure('No locationId available')); return; }
    const response: Awaited<ReturnType<typeof getSettingApi>> = yield call(getSettingApi, locationId, action.payload.key, token);
    yield put(A.fetchSettingSuccess(response.key, response.value));
  } catch (e) {
    yield put(A.fetchSettingFailure(e instanceof Error ? e.message : 'Failed to fetch setting'));
  }
}

function* fetchMultipleSettingsWorker(action: ReturnType<typeof A.fetchMultipleSettingsRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) { yield put(A.fetchMultipleSettingsFailure('No locationId available')); return; }
    const response: Awaited<ReturnType<typeof getAllSettingsApi>> = yield call(getAllSettingsApi, locationId, token);
    yield put(A.fetchMultipleSettingsSuccess(response.settings));
    if (response.orderTypes && response.orderTypes.length > 0) {
      const orderTypes = response.orderTypes.map((ot) => ({
        id: ot.id,
        name: ot.name,
        isEnabled: ot.isEnabled,
      }));
      yield put(A.fetchOrderTypesSuccess(orderTypes, locationId));
    }
  } catch (e) {
    yield put(A.fetchMultipleSettingsFailure(e instanceof Error ? e.message : 'Failed to fetch settings'));
  }
}

function* upsertSettingWorker(action: ReturnType<typeof A.upsertSettingRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) { yield put(A.upsertSettingFailure('No locationId available')); return; }
    yield call(upsertMultipleSettingsApi, locationId, [{ key: action.payload.key, value: action.payload.value }], token);
    yield put(A.upsertSettingSuccess(action.payload.key, action.payload.value));
  } catch (e) {
    yield put(A.upsertSettingFailure(e instanceof Error ? e.message : 'Failed to save setting'));
  }
}

function* upsertMultipleSettingsWorker(action: ReturnType<typeof A.upsertMultipleSettingsRequest>): SagaIterator {
  try {
    const { locationId: savedLocationId, token } = getCredentials();
    const locationId = action.payload.locationId ?? savedLocationId;
    if (!locationId) { yield put(A.upsertMultipleSettingsFailure('No locationId available')); return; }
    yield call(upsertMultipleSettingsApi, locationId, action.payload.entries, token);
    yield put(A.upsertMultipleSettingsSuccess(action.payload.entries));
  } catch (e) {
    yield put(A.upsertMultipleSettingsFailure(e instanceof Error ? e.message : 'Failed to save settings'));
  }
}

function* cateringSettingsSaga(): SagaIterator {
  yield all([
    takeLatest(CS.FETCH_REQUEST, fetchSettingWorker),
    takeLatest(CS.FETCH_BULK_REQUEST, fetchMultipleSettingsWorker),
    takeLatest(CS.UPSERT_REQUEST, upsertSettingWorker),
    takeLatest(CS.UPSERT_BULK_REQUEST, upsertMultipleSettingsWorker),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// COMMUNICATION TEMPLATES SAGA
// ════════════════════════════════════════════════════════════════════════════════

function* fetchCommTemplatesWorker(
  action: ReturnType<typeof A.fetchCommTemplatesRequest>,
): SagaIterator {
  try {
    const { locationId: storedId, token } = getCredentials();
    const locationId = action.payload.locationId ?? storedId;
    const templates: ApiCommTemplate[] = yield call(listCommTemplatesApi, locationId, token);
    yield put(A.fetchCommTemplatesSuccess(templates, locationId));
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to fetch communication templates';
    yield put(A.fetchCommTemplatesFailure(msg));
  }
}

function* upsertCommTemplateWorker(
  action: ReturnType<typeof A.upsertCommTemplateRequest>,
): SagaIterator {
  try {
    const { locationId: storedId, token: storedToken } = getCredentials();
    const locationId = action.payload.locationId ?? storedId;
    const token = action.payload.token ?? storedToken;
    const templates: ApiCommTemplate[] = yield call(
      upsertCommTemplateApi,
      locationId,
      action.payload.template,
      token,
    );
    yield put(A.upsertCommTemplateSuccess(templates));
  } catch (err: unknown) {
    const is404 = (err as { status?: number }).status === 404;
    const msg = err instanceof Error ? err.message : 'Failed to save communication template';
    yield put(A.upsertCommTemplateFailure(msg, is404));
  }
}

function* restoreCommTemplateWorker(
  action: ReturnType<typeof A.restoreCommTemplateRequest>,
): SagaIterator {
  try {
    const { locationId: storedId, token: storedToken } = getCredentials();
    const locationId = action.payload.locationId ?? storedId;
    const token = action.payload.token ?? storedToken;
    const templates: ApiCommTemplate[] = yield call(
      restoreCommTemplateApi,
      locationId,
      action.payload.eventType,
      token,
    );
    yield put(A.restoreCommTemplateSuccess(templates));
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to restore communication template';
    yield put(A.restoreCommTemplateFailure(msg));
  }
}

function* fetchTemplateTagsWorker(
  action: ReturnType<typeof A.fetchTemplateTagsRequest>,
): SagaIterator {
  const { eventType } = action.payload;
  try {
    const { token: storedToken } = getCredentials();
    const token = action.payload.token ?? storedToken;
    const res: TemplateTagsResponse = yield call(getTemplateTagsApi, eventType, token);
    yield put(A.fetchTemplateTagsSuccess(eventType, res.tags));
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Failed to fetch template tags';
    yield put(A.fetchTemplateTagsFailure(eventType, msg));
  }
}

function* cateringCommTemplatesSaga(): SagaIterator {
  yield all([
    takeLatest(CCT.FETCH_REQUEST, fetchCommTemplatesWorker),
    takeEvery(CCT.UPSERT_REQUEST, upsertCommTemplateWorker),
    takeLatest(CCT.RESTORE_REQUEST, restoreCommTemplateWorker),
    takeEvery(CCT.FETCH_TAGS_REQUEST, fetchTemplateTagsWorker),
  ]);
}

// ════════════════════════════════════════════════════════════════════════════════
// RESOLVED NOTIFICATION TEMPLATE SAGA
// ════════════════════════════════════════════════════════════════════════════════

function* fetchResolvedTemplateWorker(
  action: ReturnType<typeof A.fetchResolvedTemplateRequest>,
): SagaIterator {
  const { entity, id, country } = action.payload;
  const key = resolvedTemplateKey(entity, id, country);
  try {
    const { token: storedToken } = getCredentials();
    const token = action.payload.token ?? storedToken;
    const data: ResolvedNotificationTemplate = yield call(resolveNotificationTemplateApi, entity, id, country, token);
    yield put(A.fetchResolvedTemplateSuccess(data, key));
  } catch (err: unknown) {
    const notConfigured = (err as { status?: number }).status === 404;
    const msg = err instanceof Error ? err.message : 'Failed to resolve notification template';
    yield put(A.fetchResolvedTemplateFailure(msg, notConfigured, key));
  }
}

function* cateringResolvedTemplateSaga(): SagaIterator {
  yield takeLatest(CRT.FETCH_REQUEST, fetchResolvedTemplateWorker);
}

// ════════════════════════════════════════════════════════════════════════════════
// TAXES SAGA
// ════════════════════════════════════════════════════════════════════════════════

function getToken(): string | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS);
    if (!raw) return null;
    const cred = JSON.parse(raw);
    return cred?.credentials?.accessToken ?? cred?.accessToken ?? cred?.access_token ?? null;
  } catch {
    return null;
  }
}

function* fetchTaxesWorker(action: FetchTaxesRequestAction) {
  try {
    const { orderTypeId } = action.payload;
    if (!orderTypeId) return;

    const token = getToken();
    const data: Awaited<ReturnType<typeof fetchTaxesApi>> = yield call(
      fetchTaxesApi,
      orderTypeId,
      token,
    );

    const taxes: TaxItem[] = (data.items ?? []).filter((i) => i.isDefault);
    const charges: TaxItem[] = (data.items ?? []).filter((i) => !i.isDefault);

    yield put(A.fetchTaxesSuccess(taxes, charges, orderTypeId));
  } catch (err: any) {
    yield put(A.fetchTaxesFailure(err.message || 'Failed to fetch taxes'));
  }
}

function* cateringTaxesSaga() {
  yield takeLatest(CT.FETCH_REQUEST, fetchTaxesWorker);
}

// ════════════════════════════════════════════════════════════════════════════════
// ROOT CATERING SAGA
// ════════════════════════════════════════════════════════════════════════════════

/** Single root saga for the whole catering module. */
export function* cateringSaga(): SagaIterator {
  yield all([
    fork(cateringLeadsSaga),
    fork(cateringEstimatesSaga),
    fork(cateringOrderTypesSaga),
    fork(cateringSettingsSaga),
    fork(cateringOrdersSaga),
    fork(cateringCommTemplatesSaga),
    fork(cateringCustomersSaga),
    fork(cateringTaxesSaga),
    fork(cateringInvoicesSaga),
    fork(cateringResolvedTemplateSaga),
    fork(cateringPackagesSaga),
    fork(cateringAccessSaga),
  ]);
}
